<template>
  <div class="organization-dashboard">
    <!-- Loading skeleton -->
    <div v-if="loading && !data" class="dashboard-loading">
      <v-skeleton-loader type="card" class="mb-4" />
      <div class="d-flex" style="gap: 16px">
        <v-skeleton-loader v-for="n in 6" :key="n" type="card" style="flex: 1" />
      </div>
      <v-skeleton-loader type="image" class="mt-4" />
    </div>

    <template v-else-if="data">
      <!-- Loading overlay for period/scope switch -->
      <v-overlay :value="loading" absolute color="white" opacity="0.6" z-index="2">
        <v-progress-circular indeterminate color="#FF5722" size="40" />
      </v-overlay>

      <!-- Period selector -->
      <div class="dashboard-toolbar">
        <div></div>
        <div class="d-flex align-center" style="gap: 8px">
          <v-tooltip v-if="showReport" bottom>
            <template #activator="{ on }">
              <v-btn icon small v-on="on" @click="annualReportDialog = true">
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('org-dashboard.download-annual-report') }}</span>
          </v-tooltip>
          <annual-report-dialog
            v-if="showReport"
            v-model="annualReportDialog"
            :organization-id="organizationId"
            :scope="scope"
          />
          <dashboard-period-selector
            :period.sync="period"
            :scope.sync="scope"
            :show-scope="true"
          />
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════ -->
      <!-- SECTION 1 — Vue d'ensemble                            -->
      <!-- ═══════════════════════════════════════════════════════ -->
      <div class="section-divider mb-4">
        <v-icon color="#FF5722" small>mdi-view-dashboard</v-icon>
        <h2 class="section-divider-title">{{ $t('org-dashboard.section-overview') }}</h2>
      </div>

      <!-- Smart Insights -->
      <dashboard-smart-insights
        v-if="!hideAvailability"
        :insights="insightsData"
        class="mb-3"
      />

      <!-- Fleet Summary Line -->
      <div v-if="fleetSummaryText && !hideAvailability" class="fleet-summary-line mb-5">
        <v-icon x-small color="#6B7280" class="mr-1">mdi-ev-station</v-icon>
        <span>{{ fleetSummaryText }}</span>
      </div>

      <!-- ═══════════════════════════════════════════════════════ -->
      <!-- TABS                                                     -->
      <!-- ═══════════════════════════════════════════════════════ -->
      <v-tabs v-model="dashboardTab" class="dashboard-tabs mb-4" background-color="transparent" slider-color="#FF5722" slider-size="3">
        <v-tab v-if="showFleetActivity">
          <v-icon small class="mr-1">mdi-chart-bar</v-icon>
          {{ $t('org-dashboard.tab-my-chargers') }}
        </v-tab>
        <v-tab v-if="showDrivers">
          <v-icon small class="mr-1">mdi-account-group</v-icon>
          {{ $t('org-dashboard.tab-my-drivers') }}
        </v-tab>
        <v-tab v-if="showOperations">
          <v-icon small class="mr-1">mdi-wrench-outline</v-icon>
          {{ $t('org-dashboard.tab-operations') }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="dashboardTab" class="dashboard-tabs-items">
        <!-- ═══════════════════════════════════════════════════════ -->
        <!-- TAB 1 — Activité du Parc (Business / Pilotage)        -->
        <!-- ═══════════════════════════════════════════════════════ -->
        <v-tab-item>
          <!-- KPI Cards — Activité du parc -->
          <div v-if="activityKpiCards.length" class="kpi-grid kpi-grid--tab mb-5">
            <dashboard-kpi-card
              v-for="(kpi, i) in activityKpiCards"
              :key="'a'+i"
              :label="kpi.label"
              :value="kpi.value"
              :unit="kpi.unit"
              :icon="kpi.icon"
              :icon-color="kpi.iconColor"
              :change="kpi.change"
              :change-inverted="kpi.changeInverted"
              :danger="kpi.danger"
              :badge="kpi.badge"
              :category="kpi.category"
              :dimmed="kpi.dimmed"
              :tooltip="kpi.tooltip"
              :clickable="!!kpi.section"
              @click="kpi.section ? openDetail(kpi.section, kpi.label) : null"
            />
          </div>

          <!-- Usage Réel -->
          <div class="section-divider mb-4">
            <v-icon color="#3B82F6" small>mdi-chart-line</v-icon>
            <h2 class="section-divider-title">{{ $t('org-dashboard.section-usage') }}</h2>
          </div>

          <!-- Fleet Efficiency -->
          <dashboard-fleet-efficiency
            v-if="fleetEfficiencyData"
            :eligible-total="fleetEfficiencyData.eligibleTotal"
            :counts-by-usage-tier="fleetEfficiencyData.countsByUsageTier"
            :percent-by-usage-tier="fleetEfficiencyData.percentByUsageTier"
            :top-low-used="fleetEfficiencyData.topLowUsed"
            :top-never-used="fleetEfficiencyData.topNeverUsed"
            :top-high-used="fleetEfficiencyData.topHighUsed"
            :low-threshold="fleetEfficiencyData.lowThreshold"
            :high-threshold="fleetEfficiencyData.highThreshold"
            @tier-click="openEfficiencyDrilldown"
            @view-all="openEfficiencyDrilldown"
            @charger-click="openChargerDrawer"
            class="mb-5"
          />

          <dashboard-usage-chart
            :title="$t('org-dashboard.usage-intelligence')"
            :subtitle="usageChartSubtitle"
            :series="usageChartSeries"
            :y-axes="usageChartYAxes"
            :view-modes="usageViewModes"
            :active-view-mode.sync="usageMetric"
            :height="320"
            class="mb-5"
          />

          <!-- Consumption Breakdown + Usage Split -->
          <div class="performance-grid mb-5">
            <dashboard-consumption-breakdown
              :total="consumptionData.total"
              :total-unit="consumptionData.totalUnit"
              :segments="consumptionData.segments"
              :view-modes="consumptionViewModes"
              :active-view-mode.sync="consumptionMode"
            />
            <dashboard-usage-split
              v-if="usageSplitData"
              :internal-value="usageSplitData.internalValue"
              :external-value="usageSplitData.externalValue"
              :internal-percent="usageSplitData.internalPercent"
              :external-percent="usageSplitData.externalPercent"
              :unit="usageSplitData.unit"
              :view-modes="usageSplitViewModes"
              :active-view-mode.sync="usageSplitMode"
            />
          </div>

          <!-- Revenue -->
          <dashboard-revenue-split
            :total="revenueData.total"
            :currency="revenueData.currency"
            :change="revenueData.change"
            :segments="revenueData.segments"
            @view-report="openDetail('revenue', $t('org-dashboard.revenue-split'))"
            class="mb-5"
          />

          <!-- Heatmap -->
          <dashboard-activity-heatmap
            :raw-data="heatmapRawData"
            :class="heatmapPeakInsight ? 'mb-2' : 'mb-5'"
          />
          <div v-if="heatmapPeakInsight" class="heatmap-insight mb-5">
            <v-icon x-small color="#F59E0B" class="mr-1">mdi-lightbulb-on-outline</v-icon>
            <span>{{ heatmapPeakInsight }}</span>
          </div>

          <!-- Peak Load -->
          <dashboard-peak-load
            :slots="peakLoadSlots"
            :view-modes="peakLoadViewModes"
            :active-view-mode.sync="peakLoadMetric"
            :loading="peakLoadLoading"
            @charger-click="openChargerDrawer($event.chargingPointId)"
            class="mb-5"
          />

          <!-- Impact Écologique -->
          <div class="section-divider section-divider--esg mb-4">
            <div>
              <div class="section-divider-row">
                <v-icon color="#059669" small>mdi-leaf</v-icon>
                <h2 class="section-divider-title">{{ $t('org-dashboard.section-esg') }}</h2>
              </div>
              <div class="section-divider-subtitle">{{ $t('org-dashboard.section-esg-subtitle') }}</div>
            </div>
          </div>

          <dashboard-eco-impact
            :data="ecoData"
            :loading="ecoLoading"
            :driver-member-map="ecoDriverMemberMap"
            hide-leaderboard
            @driver-click="openMemberDrawer($event)"
            class="mb-5"
          />
        </v-tab-item>

        <!-- ═══════════════════════════════════════════════════════ -->
        <!-- TAB 2 — Conducteurs                                    -->
        <!-- ═══════════════════════════════════════════════════════ -->
        <v-tab-item>
          <!-- KPI Cards — Conducteurs -->
          <div v-if="driverKpiCards.length" class="kpi-grid kpi-grid--tab mb-5">
            <dashboard-kpi-card
              v-for="(kpi, i) in driverKpiCards"
              :key="'d'+i"
              :label="kpi.label"
              :value="kpi.value"
              :unit="kpi.unit"
              :icon="kpi.icon"
              :icon-color="kpi.iconColor"
              :change="kpi.change"
              :change-inverted="kpi.changeInverted"
              :danger="kpi.danger"
              :badge="kpi.badge"
              :category="kpi.category"
              :dimmed="kpi.dimmed"
              :tooltip="kpi.tooltip"
              :clickable="!!kpi.section"
              @click="kpi.section ? openDetail(kpi.section, kpi.label) : null"
            />
          </div>

          <!-- Consommation Conducteurs -->
          <div class="section-divider mb-4">
            <v-icon color="#3B82F6" small>mdi-chart-areaspline</v-icon>
            <h2 class="section-divider-title">{{ $t('org-dashboard.section-driver-consumption') }}</h2>
          </div>

          <dashboard-driver-consumption-chart
            :data="driverConsumptionTimeline"
            :interval="driverConsumptionInterval"
            class="mb-5"
          />

          <dashboard-driver-consumption-breakdown
            v-if="driverConsumptionBreakdown"
            :segments="driverConsumptionBreakdown.segments || []"
            :total-kwh="driverConsumptionBreakdown.totalKwh || 0"
            :total-cost="driverConsumptionBreakdown.totalCost || 0"
            class="mb-5"
          />

          <!-- Top Conducteurs & Performances -->
          <div class="section-divider mb-4">
            <v-icon color="#8B5CF6" small>mdi-account-group</v-icon>
            <h2 class="section-divider-title">{{ $t('org-dashboard.section-drivers') }}</h2>
          </div>

          <dashboard-driver-distribution
            :organization-id="organizationId"
            :period="period"
            :scope="scope"
            :segment="driverSegment"
            :drivers="topDrivers"
            :total-drivers="topDriversTotal ? topDriversTotal.users : 0"
            :active-drivers="data.kpis && data.kpis.activeUsers ? data.kpis.activeUsers.value : 0"
            :adoption-rate="adoptionRateValue || 0"
            @update:metric="driverMetric = $event"
            @update:segment="driverSegment = $event; loadTopDrivers()"
            @member-click="openMemberDrawer($event)"
            class="mb-5"
          />

          <!-- Tendance d'adoption -->
          <dashboard-adoption-trend
            v-if="adoptionTrendData.length"
            :data="adoptionTrendData"
            class="mb-5"
          />

          <!-- Top Conducteurs Écologiques -->
          <dashboard-eco-drivers
            v-if="ecoData && ecoData.topDrivers && ecoData.topDrivers.length"
            :drivers="ecoData.topDrivers"
            :eco-impact="ecoData.ecoImpact"
            :eco-equivalents="ecoData.ecoEquivalents"
            :trend="ecoData.driverTrend"
            :loading="ecoLoading"
            :driver-member-map="ecoDriverMemberMap"
            @driver-click="openMemberDrawer($event)"
            @view-all="openEcoDriversDrilldown"
            class="mb-5"
          />

          <!-- Finances Conducteurs -->
          <div class="section-divider mb-4">
            <v-icon color="#FF5722" small>mdi-cash-multiple</v-icon>
            <h2 class="section-divider-title">{{ $t('org-dashboard.section-finances') }}</h2>
          </div>

          <dashboard-cost-analysis
            :segments="costAnalysisData.segments"
            :sparkline-data="costAnalysisData.sparklineData"
            :sparkline-labels="costAnalysisData.sparklineLabels"
            :currency="costAnalysisData.currency"
            @view-details="openDetail('costs', $t('org-dashboard.cost-analysis'))"
            @card-click="onCostCardClick"
            class="mb-5"
          />

          <dashboard-cost-forecast
            v-if="costForecastData"
            :monthly-run-rate="costForecastData.monthlyRunRate"
            :annual-forecast="costForecastData.annualForecast"
            :spent="costForecastData.spent"
            :period-days="costForecastData.periodDays"
            :currency="costForecastData.currency"
            class="mb-5"
          />
        </v-tab-item>

        <!-- ═══════════════════════════════════════════════════════ -->
        <!-- TAB 3 — Exploitation                                   -->
        <!-- ═══════════════════════════════════════════════════════ -->
        <v-tab-item>
          <!-- KPI Cards — Exploitation -->
          <div v-if="exploitationKpiCards.length" class="kpi-grid kpi-grid--tab mb-5">
            <dashboard-kpi-card
              v-for="(kpi, i) in exploitationKpiCards"
              :key="'e'+i"
              :label="kpi.label"
              :value="kpi.value"
              :unit="kpi.unit"
              :icon="kpi.icon"
              :icon-color="kpi.iconColor"
              :change="kpi.change"
              :change-inverted="kpi.changeInverted"
              :danger="kpi.danger"
              :badge="kpi.badge"
              :category="kpi.category"
              :dimmed="kpi.dimmed"
              :tooltip="kpi.tooltip"
              :clickable="!!kpi.section"
              @click="kpi.section ? openDetail(kpi.section, kpi.label) : null"
            />
          </div>

          <!-- Disponibilité opérationnelle -->
          <div class="section-divider mb-4">
            <v-icon color="#EF4444" small>mdi-shield-check</v-icon>
            <h2 class="section-divider-title">{{ $t('org-dashboard.section-fleet-health') }}</h2>
          </div>

          <!-- Ligne 1 : Disponibilité opérationnelle (2/3) + Fiabilité (1/3) -->
          <div class="ops-row-health mb-5">
            <dashboard-fleet-health
              v-if="fleetHealthData"
              :operational-availability="fleetHealthData.operationalAvailability"
              :eligible="fleetHealthData.eligible"
              :excluded="fleetHealthData.excluded"
              :excluded-breakdown="fleetHealthData.excludedBreakdown"
              :by-severity="fleetHealthData.bySeverity"
              :offline-histogram="fleetHealthData.offlineHistogram"
              :backlog-incident="fleetHealthData.backlogIncident"
              :backlog-critical="fleetHealthData.backlogCritical"
              @severity-click="openHealthDrilldown"
              @view-all="openHealthDrilldown"
              @charger-click="openChargerDrawer"
              class="ops-col-health"
            />
            <dashboard-reliability
              :data="reliabilityData"
              :loading="reliabilityLoading"
              class="ops-col-reliability"
            />
          </div>

          <!-- Ligne 2 : Fleet Status + Actions prioritaires -->
          <div class="fleet-actions-grid mb-5">
            <dashboard-fleet-status
              :total="fleetStatusData.total"
              :segments="fleetStatusData.segments"
              :alert="fleetStatusData.alert"
              :meter-devices="fleetStatusData.meterDevices"
              @view-detail="openDetail('fleet', $t('org-dashboard.fleet-status'))"
              class="fleet-col"
            />
            <dashboard-action-items
              :items="actionItemsData"
              @action="handleAction"
              class="actions-col"
            />
          </div>

          <!-- Ligne 3 : Tendance dispo + Qualité des données -->
          <div class="ops-row-trend-quality mb-5">
            <dashboard-health-trend
              :data="healthTrendData"
              class="ops-col-trend"
            />
            <dashboard-data-quality
              v-if="dataQualityData"
              :score="dataQualityData.score"
              :status="dataQualityData.status"
              :breakdown="dataQualityData.breakdown"
              :total-problematic="dataQualityData.totalProblematic"
              :top-problematic="dataQualityData.topProblematic"
              @view-all="openDataQualityDrilldown"
              @charger-click="openChargerDrawer"
              class="ops-col-quality"
            />
          </div>

          <!-- Ligne 4 : Historique incidents -->
          <dashboard-incident-timeline
            :items="incidents"
            :total-items="incidentsSummary ? incidentsSummary.totalIncidents : 0"
            :summary="incidentsSummary"
            :loading="incidentsLoading"
            @charger-click="openChargerDrawer"
            @view-all="openIncidentsDrilldown"
            class="mb-5"
          />

          <!-- Ligne 5 : Inventaire technique -->
          <dashboard-fleet-inventory
            v-if="fleetInventoryData && fleetInventoryData.firmware"
            :firmware="fleetInventoryData.firmware"
            @firmware-drilldown="openFirmwareDrilldown"
            class="mb-5"
          />

          <!-- Ligne 6 : SIM -->
          <dashboard-sim-usage
            v-if="simUsageData"
            :sim-status="fleetInventoryData ? fleetInventoryData.sim : null"
            :summary="simUsageData.summary"
            :top-consumers="simUsageData.topConsumers"
            :usage-trend="simUsageData.usageTrend"
            :distribution="simUsageData.distribution"
            :loading="simUsageLoading"
            @charger-click="openChargerDrawer"
            @sim-drilldown="openSimDrilldown"
            class="mb-5"
          />
        </v-tab-item>
      </v-tabs-items>

      <!-- Detail Dialog -->
      <dashboard-detail-dialog
        v-model="detailDialog.open"
        :title="detailDialog.title"
        :headers="detailDialog.headers"
        :items="detailDialog.items"
        :total-items="detailDialog.totalItems"
        :options="detailDialog.options"
        :loading="detailDialog.loading"
        :kpi-cards="detailDialog.kpiCards"
        :no-data-text="detailDialog.section === 'refunds' ? $t('org-dashboard.refunds-no-data') : undefined"
        @update:options="loadDetailPage"
        @click:link="onDetailLinkClick"
      />

      <!-- Health Drilldown Dialog -->
      <dashboard-health-drilldown
        v-model="healthDrilldown.open"
        :category="healthDrilldown.category"
        :items="healthDrilldown.items"
        :total-items="healthDrilldown.totalItems"
        :loading="healthDrilldown.loading"
        :kpi-cards="healthDrilldown.kpiCards"
        @update:category="onHealthCategoryChange"
        @update:options="loadHealthDrilldownPage"
        @charger-click="openChargerDrawer"
      />

      <dashboard-efficiency-drilldown
        v-model="efficiencyDrilldown.open"
        :tier="efficiencyDrilldown.tier"
        :items="efficiencyDrilldown.items"
        :total-items="efficiencyDrilldown.totalItems"
        :loading="efficiencyDrilldown.loading"
        @update:tier="onEfficiencyTierChange"
        @update:options="loadEfficiencyDrilldownPage"
        @charger-click="openChargerDrawer"
      />

      <dashboard-data-quality-drilldown
        v-model="dataQualityDrilldown.open"
        :items="dataQualityDrilldown.items"
        :total-items="dataQualityDrilldown.totalItems"
        :loading="dataQualityDrilldown.loading"
        @update:options="loadDataQualityDrilldownPage"
        @charger-click="openChargerDrawer"
      />

      <!-- Eco Drivers Drilldown -->
      <dashboard-eco-drivers-drilldown
        v-model="ecoDriversDrilldown.open"
        :items="ecoDriversDrilldown.items"
        :total-items="ecoDriversDrilldown.totalItems"
        :totals="ecoDriversDrilldown.totals"
        :loading="ecoDriversDrilldown.loading"
        @update:options="loadEcoDriversDrilldownPage"
        @member-click="openMemberDrawer($event)"
      />

      <!-- Firmware Drilldown -->
      <dashboard-firmware-drilldown
        v-model="firmwareDrilldown.open"
        :vendor="firmwareDrilldown.vendor"
        :model="firmwareDrilldown.model"
        :version="firmwareDrilldown.version"
        :status="firmwareDrilldown.status"
        :summary="firmwareDrilldown.summary"
        :items="firmwareDrilldown.items"
        :total-items="firmwareDrilldown.totalItems"
        :loading="firmwareDrilldown.loading"
        @update:version="onFirmwareVersionChange"
        @update:options="loadFirmwareDrilldownPage"
        @charger-click="openChargerDrawer"
      />

      <!-- SIM Drilldown -->
      <dashboard-sim-drilldown
        v-model="simDrilldown.open"
        :sim-type="simDrilldown.simType"
        :bucket="simDrilldown.bucket"
        :summary="simDrilldown.summary"
        :items="simDrilldown.items"
        :total-items="simDrilldown.totalItems"
        :loading="simDrilldown.loading"
        @update:sim-type="onSimTypeChange"
        @update:options="loadSimDrilldownPage"
        @charger-click="openChargerDrawer"
      />

      <!-- Incidents Drilldown -->
      <v-dialog v-model="incidentsDrilldown.open" max-width="900" scrollable>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="#f97316" class="mr-2">mdi-alert-circle-outline</v-icon>
            {{ $t('org-dashboard.incident-timeline-title') }}
            <v-spacer />
            <v-btn icon @click="incidentsDrilldown.open = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="incidentsDrilldownHeaders"
              :items="incidentsDrilldown.items"
              :server-items-length="incidentsDrilldown.totalItems"
              :options.sync="incidentsDrilldown.options"
              :loading="incidentsDrilldown.loading"
              :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
              @update:options="loadIncidentsDrilldownPage"
              dense
            >
              <template #item.identifier="{ item }">
                <a class="primary--text font-weight-bold" style="cursor:pointer" @click="openChargerDrawer(item.chargingPointId)">{{ item.identifier }}</a>
              </template>
              <template #item.severity="{ item }">
                <v-chip x-small :color="item.severity === 'critical' ? '#fef2f2' : '#fff7ed'" :text-color="item.severity === 'critical' ? '#dc2626' : '#c2410c'" label>
                  {{ item.severity }}
                </v-chip>
              </template>
              <template #item.startedAt="{ item }">
                {{ formatIncidentDate(item.startedAt) }}
              </template>
              <template #item.durationHours="{ item }">
                <span v-if="item.durationHours" class="font-weight-bold">{{ formatIncidentDuration(item.durationHours) }}</span>
                <span v-else>-</span>
              </template>
              <template #item.status="{ item }">
                <v-chip x-small :color="item.resolvedAt ? '#ecfdf5' : '#fef2f2'" :text-color="item.resolvedAt ? '#059669' : '#dc2626'" label>
                  {{ item.resolvedAt ? $t('org-dashboard.incident-resolved') : $t('org-dashboard.incident-open') }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Charger Drawer -->
      <dashboard-charger-drawer
        v-model="chargerDrawer.open"
        :data="chargerDrawer.data"
        :loading="chargerDrawer.loading"
        :organization-id="organizationId"
        @member-click="openMemberDrawer"
      />

      <!-- Member Drawer -->
      <dashboard-member-drawer
        v-model="memberDrawer.open"
        :data="memberDrawer.data"
        :loading="memberDrawer.loading"
        :organization-id="organizationId"
        :member-id="memberDrawer.memberId"
      />
    </template>

    <!-- Error state -->
    <div v-else-if="error" class="dashboard-error">
      <v-icon large color="grey lighten-1">mdi-alert-circle-outline</v-icon>
      <p>{{ $t('org-dashboard.load-error') }}</p>
      <v-btn small outlined @click="refresh">{{ $t('org-dashboard.retry') }}</v-btn>
    </div>
  </div>
</template>

<script>
import OrganizationDashboardRepository from '@/repository/OrganizationDashboardRepository'
import DashboardPeriodSelector from '@blocks/dashboard/DashboardPeriodSelector.vue'
import AnnualReportDialog from './AnnualReportDialog.vue'
import DashboardSmartInsights from '@blocks/dashboard/DashboardSmartInsights.vue'
import DashboardKpiCard from '@blocks/dashboard/DashboardKpiCard.vue'
import DashboardUsageChart from '@blocks/dashboard/DashboardUsageChart.vue'
import DashboardActivityHeatmap from '@blocks/dashboard/DashboardActivityHeatmap.vue'
import DashboardFleetStatus from '@blocks/dashboard/DashboardFleetStatus.vue'
import DashboardActionItems from '@blocks/dashboard/DashboardActionItems.vue'
import DashboardDataQuality from '@blocks/dashboard/DashboardDataQuality.vue'
import DashboardRevenueSplit from '@blocks/dashboard/DashboardRevenueSplit.vue'
import DashboardConsumptionBreakdown from '@blocks/dashboard/DashboardConsumptionBreakdown.vue'
import DashboardCostAnalysis from '@blocks/dashboard/DashboardCostAnalysis.vue'
import DashboardDriverDistribution from '@blocks/dashboard/DashboardDriverDistribution.vue'
import DashboardCostForecast from '@blocks/dashboard/DashboardCostForecast.vue'
import DashboardDetailDialog from '@blocks/dashboard/DashboardDetailDialog.vue'
import DashboardPeakLoad from '@blocks/dashboard/DashboardPeakLoad.vue'
import DashboardChargerDrawer from '@blocks/dashboard/DashboardChargerDrawer.vue'
import DashboardUsageSplit from '@blocks/dashboard/DashboardUsageSplit.vue'
import DashboardEcoImpact from '@blocks/dashboard/DashboardEcoImpact.vue'
import DashboardMemberDrawer from '@blocks/dashboard/DashboardMemberDrawer.vue'
import DashboardFleetHealth from '@blocks/dashboard/DashboardFleetHealth.vue'
import DashboardHealthDrilldown from '@blocks/dashboard/DashboardHealthDrilldown.vue'
import DashboardFleetEfficiency from '@blocks/dashboard/DashboardFleetEfficiency.vue'
import DashboardEfficiencyDrilldown from '@blocks/dashboard/DashboardEfficiencyDrilldown.vue'
import DashboardDataQualityDrilldown from '@blocks/dashboard/DashboardDataQualityDrilldown.vue'
import DashboardEcoDriversDrilldown from '@blocks/dashboard/DashboardEcoDriversDrilldown.vue'
import DashboardHealthTrend from '@blocks/dashboard/DashboardHealthTrend.vue'
import DashboardReliability from '@blocks/dashboard/DashboardReliability.vue'
import DashboardIncidentTimeline from '@blocks/dashboard/DashboardIncidentTimeline.vue'
import DashboardFleetInventory from '@blocks/dashboard/DashboardFleetInventory.vue'
import DashboardSimUsage from '@blocks/dashboard/DashboardSimUsage.vue'
import DashboardFirmwareDrilldown from '@blocks/dashboard/DashboardFirmwareDrilldown.vue'
import DashboardSimDrilldown from '@blocks/dashboard/DashboardSimDrilldown.vue'
import DashboardAdoptionTrend from '@blocks/dashboard/DashboardAdoptionTrend.vue'
import DashboardEcoDrivers from '@blocks/dashboard/DashboardEcoDrivers.vue'
import DashboardDriverConsumptionChart from '@blocks/dashboard/DashboardDriverConsumptionChart.vue'
import DashboardDriverConsumptionBreakdown from '@blocks/dashboard/DashboardDriverConsumptionBreakdown.vue'

export default {
  name: 'OrganizationDashboard',

  components: {
    AnnualReportDialog,
    DashboardPeriodSelector,
    DashboardSmartInsights,
    DashboardKpiCard,
    DashboardUsageChart,
    DashboardActivityHeatmap,
    DashboardFleetStatus,
    DashboardActionItems,
    DashboardDataQuality,
    DashboardRevenueSplit,
    DashboardConsumptionBreakdown,
    DashboardCostAnalysis,
    DashboardDriverDistribution,
    DashboardCostForecast,
    DashboardDetailDialog,
    DashboardPeakLoad,
    DashboardChargerDrawer,
    DashboardUsageSplit,
    DashboardEcoImpact,
    DashboardMemberDrawer,
    DashboardFleetHealth,
    DashboardHealthDrilldown,
    DashboardFleetEfficiency,
    DashboardEfficiencyDrilldown,
    DashboardDataQualityDrilldown,
    DashboardEcoDriversDrilldown,
    DashboardHealthTrend,
    DashboardReliability,
    DashboardIncidentTimeline,
    DashboardFleetInventory,
    DashboardSimUsage,
    DashboardFirmwareDrilldown,
    DashboardSimDrilldown,
    DashboardAdoptionTrend,
    DashboardEcoDrivers,
    DashboardDriverConsumptionChart,
    DashboardDriverConsumptionBreakdown,
  },

  data() {
    return {
      loading: false,
      error: false,
      data: null,
      annualReportDialog: false,

      period: '30d',
      dashboardTab: parseInt(localStorage.getItem('org_dashboard_tab') || '0'),
      scope: 'self',
      filterMode: 'hybrid',
      hideAvailability: false,
      usageMetric: 'sessions',
      consumptionMode: 'kwh',
      topDrivers: [],
      topDriversTotal: null,
      topDriversLoading: false,
      driverSegment: 'all',
      driverMetric: 'energy',
      peakLoadMetric: 'sessions',
      peakLoadSlots: [],
      peakLoadLoading: false,
      usageSplitMode: 'kwh',
      ecoData: null,
      ecoLoading: false,
      healthTrendData: [],
      healthTrendLoading: false,
      incidents: [],
      incidentsSummary: null,
      incidentsLoading: false,
      reliabilityData: null,
      reliabilityLoading: false,
      fleetInventoryData: null,
      simUsageData: null,
      simUsageLoading: false,
      driverConsumptionTimeline: [],
      driverConsumptionInterval: 'day',
      driverConsumptionBreakdown: null,
      driverConsumptionLoading: false,
      incidentsDrilldown: {
        open: false,
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 20 },
        loading: false,
      },
      chargerDrawer: {
        open: false,
        chargingPointId: null,
        data: null,
        loading: false,
      },
      memberDrawer: {
        open: false,
        memberId: null,
        data: null,
        loading: false,
      },
      healthDrilldown: {
        open: false,
        category: 'incident',
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 20 },
        loading: false,
        kpiCards: [],
      },
      efficiencyDrilldown: {
        open: false,
        tier: 'low',
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 20 },
        loading: false,
      },
      dataQualityDrilldown: {
        open: false,
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 20 },
        loading: false,
      },
      ecoDriversDrilldown: {
        open: false,
        items: [],
        totalItems: 0,
        totals: null,
        options: { page: 1, itemsPerPage: 20, sortBy: ['co2SavedKg'], sortDesc: [true] },
        loading: false,
      },
      firmwareDrilldown: {
        open: false,
        vendor: null,
        model: null,
        version: null,
        status: 'all',
        summary: null,
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 20 },
        loading: false,
      },
      simDrilldown: {
        open: false,
        simType: 'connected',
        bucket: null,
        summary: null,
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 20 },
        loading: false,
      },
      detailDialog: {
        open: false,
        title: '',
        section: '',
        headers: [],
        items: [],
        totalItems: 0,
        options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
        loading: false,
        kpiCards: [],
      },
    }
  },

  computed: {
    organizationId() {
      return this.$route.params.id
    },

    // This component is kept alive as a tab of OrganizationDetail. When the user drills into a sibling
    // detail page (e.g. /vehicles/:id), the route param `id` becomes that entity's id — NOT an org id —
    // and `organizationId` changes with it. React ONLY while we're actually on an organization route,
    // otherwise the watcher below would fire 10 dashboard fetches with a vehicle id (403 / broken page).
    isOrganizationRoute() {
      return this.$route.matched.some(m => (m.path || '').includes('organizations/:id'))
    },

    dashboardConfig() {
      return this.$parent?.organization?.dashboardConfig || null
    },

    isZeMode() {
      return !!this.currentUser?.isZE && this.$route.query.ze === '1'
    },

    showFleetActivity() {
      if (this.isZeMode) return true
      return this.dashboardConfig?.tabs?.fleetActivity ?? false
    },

    showDrivers() {
      if (this.isZeMode) return true
      return this.dashboardConfig?.tabs?.drivers ?? false
    },

    showOperations() {
      if (this.isZeMode) return true
      return this.dashboardConfig?.tabs?.operations ?? false
    },

    showReport() {
      if (this.isZeMode) return true
      return this.dashboardConfig?.enableReport ?? false
    },

    filterModeOptions() {
      return [
        { value: 'active-only', label: this.$t('org-dashboard.filter-active-only'), tooltip: this.$t('org-dashboard.filter-active-only-tooltip') },
        { value: 'all', label: this.$t('org-dashboard.filter-all'), tooltip: this.$t('org-dashboard.filter-all-tooltip') },
        { value: 'exclude-home', label: this.$t('org-dashboard.filter-exclude-home'), tooltip: this.$t('org-dashboard.filter-exclude-home-tooltip') },
        { value: 'home-only', label: this.$t('org-dashboard.filter-home-only'), tooltip: this.$t('org-dashboard.filter-home-only-tooltip') },
        { value: 'sim-connected', label: this.$t('org-dashboard.filter-sim-connected'), tooltip: this.$t('org-dashboard.filter-sim-connected-tooltip') },
        { value: 'hybrid', label: this.$t('org-dashboard.filter-hybrid'), tooltip: this.$t('org-dashboard.filter-hybrid-tooltip') },
      ]
    },

    insightsData() {
      const fh = this.data?.fleetHealth
      const si = this.data?.smartInsights

      // Operational logic when fleetHealth or enriched smartInsights is available
      if (fh || si?.operationalAvailability !== undefined) {
        const items = []
        const opAvail = si?.operationalAvailability ?? fh?.operationalAvailability ?? 0
        const needsAction = si?.needsAction ?? ((fh?.bySeverity?.incident || 0) + (fh?.bySeverity?.critical || 0))
        const excluded = fh?.excluded || 0
        const maintenanceCount = fh?.bySeverity?.maintenance || 0
        const inactiveCount = fh?.bySeverity?.inactive || 0

        // Operational availability
        const availColor = opAvail >= 95 ? '#22c55e' : opAvail >= 80 ? '#eab308' : '#EF4444'
        const belowTarget = si?.availabilityTarget && opAvail < si.availabilityTarget
        items.push({
          color: availColor,
          pulse: belowTarget,
          value: opAvail + '%',
          label: this.$t('org-dashboard.health-insight-availability'),
          subLabel: si?.availabilityTarget ? this.$t('org-dashboard.health-insight-target', { target: si.availabilityTarget }) : null,
        })

        // Needs action (incident + critical)
        if (needsAction > 0) {
          items.push({
            color: '#EF4444',
            pulse: true,
            value: needsAction,
            label: this.$t('org-dashboard.health-insight-needs-action'),
          })
        } else {
          items.push({
            color: '#22c55e',
            value: '✓',
            label: this.$t('org-dashboard.health-insight-all-ok'),
          })
        }

        // Never used chargers
        const neverUsed = si?.neverUsed ?? this.data?.fleetEfficiency?.countsByUsageTier?.never
        if (neverUsed > 0) {
          items.push({
            color: '#f59e0b',
            value: neverUsed,
            label: this.$t('org-dashboard.efficiency-tier-never').toLowerCase(),
            subLabel: this.$t('org-dashboard.insight-never-used', { count: neverUsed }),
          })
        }

        // Exclusions
        if (excluded > 0) {
          items.push({
            color: '#6b7280',
            value: excluded,
            label: this.$t('org-dashboard.health-insight-exclusions'),
            subLabel: this.$t('org-dashboard.health-insight-exclusions-detail', { maintenance: maintenanceCount, inactive: inactiveCount }),
          })
        }

        return items
      }

      // Fallback: old smartInsights logic
      if (!si) return []
      const items = []
      if (si.offlineCount > 0) {
        items.push({
          color: '#EF4444',
          pulse: si.criticalCount > 0,
          value: si.offlineCount,
          label: this.$t('org-dashboard.chargers-offline'),
          subLabel: si.criticalCount > 0 ? `${si.criticalCount} ${this.$t('org-dashboard.critical-long')}` : null,
        })
      }
      if (si.availability !== undefined) {
        items.push({
          color: '#F59E0B',
          value: si.availability + '%',
          label: this.$t('org-dashboard.availability-label'),
          subLabel: si.availabilityTarget ? `${this.$t('org-dashboard.target')} ${si.availabilityTarget}%` : null,
        })
      }
      return items
    },

    // ── KPI Cards — Tab Activité du parc ──────────────────
    activityKpiCards() {
      const kpis = this.data?.kpis
      if (!kpis) return []
      return [
        // Disponibilité opérationnelle
        ...(!this.hideAvailability ? (kpis.operationalAvailability ? [{
          label: this.$t('org-dashboard.health-availability-label'),
          value: kpis.operationalAvailability.value ?? 0,
          unit: '%',
          icon: 'mdi-shield-check',
          iconColor: this.healthAvailabilityColor,
          badge: this.$t('org-dashboard.health-availability-badge', {
            eligible: kpis.operationalAvailability.eligible || 0,
            excluded: this.data?.fleetHealth?.excluded || 0,
          }),
          tooltip: this.$t('org-dashboard.kpi-tooltip-availability'),
          category: 'critical',
        }] : [{
          label: this.$t('org-dashboard.availability-label'),
          value: kpis.availability?.value ?? 0,
          unit: '%',
          icon: 'mdi-check-circle',
          iconColor: '#10B981',
          change: kpis.availability?.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-availability'),
          category: 'critical',
        }]) : []),
        // Bornes
        ...(this.data?.fleetStatus?.total != null ? [{
          label: this.$t('org-dashboard.chargers-count'),
          value: this.data.fleetStatus.total,
          icon: 'mdi-ev-plug-type2',
          iconColor: '#0EA5E9',
          tooltip: this.$t('org-dashboard.kpi-tooltip-chargers-count'),
          section: 'fleet',
          category: 'usage',
        }] : []),
        // Sessions
        {
          label: this.$t('org-dashboard.sessions-short'),
          value: kpis.sessions?.value ?? 0,
          icon: 'mdi-ev-station',
          iconColor: '#3B82F6',
          change: kpis.sessions?.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-sessions'),
          section: 'sessions',
          category: 'usage',
        },
        // Énergie
        {
          label: this.$t('org-dashboard.energy'),
          value: this.formatLargeNumber(kpis.energy?.value ?? 0),
          unit: 'kWh',
          icon: 'mdi-flash',
          iconColor: '#F59E0B',
          change: kpis.energy?.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-energy'),
          section: 'energy',
          category: 'usage',
        },
        // Durée moyenne session
        ...(kpis.avgSessionDuration ? [{
          label: this.$t('org-dashboard.avg-session-duration'),
          value: this.formatDurationShort(kpis.avgSessionDuration.value),
          icon: 'mdi-timer-outline',
          iconColor: '#6366F1',
          change: kpis.avgSessionDuration.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-avg-session'),
          category: 'usage',
        }] : []),
        // Impact écologique
        ...((this.data?.ecoImpact || this.ecoData) ? [{
          label: this.$t('org-dashboard.eco-impact'),
          value: (this.data?.ecoImpact?.co2SavedTons ?? this.ecoData?.ecoImpact?.co2SavedTons ?? 0).toFixed(1),
          unit: 't CO₂',
          icon: 'mdi-leaf',
          iconColor: '#059669',
          change: this.data?.ecoImpact?.change ?? this.data?.ecoImpact?.changePercent ?? this.ecoData?.ecoImpact?.change ?? this.ecoData?.ecoImpact?.changePercent ?? null,
          changeInverted: true,
          tooltip: this.$t('org-dashboard.kpi-tooltip-eco'),
          section: 'eco',
          category: 'strategic',
        }] : []),
        // Saturation max
        ...((this.data?.kpis?.peakSaturation?.maxRate != null || this.data?.fleetStatus?.peakSaturation?.maxRate != null) ? [{
          label: this.$t('org-dashboard.peak-saturation'),
          value: (this.data.kpis?.peakSaturation || this.data.fleetStatus?.peakSaturation).maxRate,
          unit: '%',
          icon: 'mdi-gauge-full',
          iconColor: '#DC2626',
          badge: this.formatPeakSaturationSlot(this.data.kpis?.peakSaturation || this.data.fleetStatus?.peakSaturation),
          tooltip: this.$t('org-dashboard.kpi-tooltip-peak-saturation'),
          category: 'critical',
        }] : []),
      ]
    },

    // ── KPI Cards — Tab Conducteurs ──────────────────
    driverKpiCards() {
      const kpis = this.data?.kpis
      if (!kpis) return []
      return [
        // Utilisateurs actifs / Taux d'adoption
        ...(this.adoptionRateValue != null ? [{
          label: this.$t('org-dashboard.adoption-rate'),
          value: this.adoptionRateValue,
          unit: '%',
          icon: this.adoptionRateValue >= 100 ? 'mdi-check-decagram' : 'mdi-account-check',
          iconColor: '#8B5CF6',
          section: 'activeUsers',
          badge: this.$t('org-dashboard.adoption-rate-badge', {
            active: this.data?.kpis?.activeUsers?.value ?? 0,
            total: this.topDriversTotal?.users ?? 0,
          }),
          tooltip: this.$t('org-dashboard.kpi-tooltip-adoption'),
          category: 'strategic',
          dimmed: this.adoptionRateValue >= 100,
        }, {
          label: this.$t('org-dashboard.active-users'),
          value: kpis.activeUsers?.value ?? 0,
          icon: 'mdi-account-group',
          iconColor: '#8B5CF6',
          change: kpis.activeUsers?.change ?? null,
          section: 'activeUsers',
          category: 'strategic',
        }] : [{
          label: this.$t('org-dashboard.active-users'),
          value: kpis.activeUsers?.value ?? 0,
          icon: 'mdi-account-group',
          iconColor: '#8B5CF6',
          change: kpis.activeUsers?.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-adoption'),
          section: 'activeUsers',
          category: 'strategic',
        }]),
        // Moyen kWh par conducteur
        ...(this.topDriversTotal?.avgEnergyPerDriver != null ? [{
          label: this.$t('org-dashboard.avg-energy-per-driver'),
          value: Math.round(this.topDriversTotal.avgEnergyPerDriver),
          unit: 'kWh',
          icon: 'mdi-flash-circle',
          iconColor: '#F59E0B',
          tooltip: this.$t('org-dashboard.kpi-tooltip-avg-energy-driver'),
          category: 'usage',
        }] : []),
        // Coût moyen par conducteur
        {
          label: this.$t('org-dashboard.avg-cost-per-driver'),
          value: kpis.avgCostPerDriver?.value != null ? this.formatCurrencyValue(kpis.avgCostPerDriver.value, 'EUR') : 0,
          unit: kpis.avgCostPerDriver?.value != null ? '' : '€',
          icon: 'mdi-account-cash',
          iconColor: '#F97316',
          change: kpis.avgCostPerDriver?.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-avg-cost'),
          category: 'strategic',
        },
        // Dépendance itinérance
        ...(this.roamingDependencyPct != null ? [{
          label: this.$t('org-dashboard.roaming-dependency'),
          value: this.roamingDependencyPct,
          unit: '%',
          icon: 'mdi-swap-horizontal',
          iconColor: '#F59E0B',
          tooltip: this.$t('org-dashboard.kpi-tooltip-roaming-dependency'),
          category: 'strategic',
        }] : []),
        // Coût total période
        ...(this.totalPeriodCost != null ? [{
          label: this.$t('org-dashboard.total-period-cost'),
          value: this.formatCurrencyValue(this.totalPeriodCost, 'EUR'),
          icon: 'mdi-cash-multiple',
          iconColor: '#DC2626',
          tooltip: this.$t('org-dashboard.kpi-tooltip-total-cost'),
          section: 'costs',
          category: 'strategic',
        }] : []),
      ]
    },

    // ── KPI Cards — Tab Exploitation ──────────────────
    exploitationKpiCards() {
      const kpis = this.data?.kpis
      if (!kpis) return []
      const needsAction = (this.data?.fleetHealth?.bySeverity?.incident || 0) + (this.data?.fleetHealth?.bySeverity?.critical || 0)
      return [
        // Nécessitent action
        ...(!this.hideAvailability ? (this.data?.fleetHealth ? [{
          label: this.$t('org-dashboard.health-needs-action'),
          value: needsAction,
          icon: 'mdi-alert-circle-outline',
          iconColor: needsAction > 0 ? '#EF4444' : '#10B981',
          danger: needsAction > 0,
          tooltip: this.$t('org-dashboard.kpi-tooltip-needs-action'),
          section: 'offline',
          category: 'critical',
        }] : [{
          label: this.$t('org-dashboard.offline-label'),
          value: kpis.offline?.value ?? 0,
          icon: 'mdi-wifi-off',
          iconColor: '#EF4444',
          change: kpis.offline?.change ?? null,
          changeInverted: true,
          danger: (kpis.offline?.value ?? 0) > 0,
          tooltip: this.$t('org-dashboard.kpi-tooltip-needs-action'),
          section: 'offline',
          category: 'critical',
        }]) : []),
        // Admin (ZE only) — raw availability
        ...(this.currentUser?.isZE && kpis.availability ? [{
          label: this.$t('org-dashboard.availability-label'),
          value: kpis.availability.value ?? 0,
          unit: '%',
          icon: 'mdi-check-circle',
          iconColor: '#10B981',
          change: kpis.availability.change ?? null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-availability-raw'),
          category: 'critical',
        }] : []),
        // Admin (ZE only) — raw offline
        ...(this.currentUser?.isZE && kpis.offline ? [{
          label: this.$t('org-dashboard.offline-label'),
          value: kpis.offline.value ?? 0,
          icon: 'mdi-wifi-off',
          iconColor: '#EF4444',
          change: kpis.offline.change ?? null,
          changeInverted: true,
          danger: (kpis.offline.value ?? 0) > 0,
          tooltip: this.$t('org-dashboard.kpi-tooltip-offline'),
          section: 'offline',
          category: 'critical',
        }] : []),
        // Anomalies
        {
          label: this.$t('org-dashboard.anomalies'),
          value: kpis.anomalies?.value ?? 0,
          icon: 'mdi-alert-circle-outline',
          iconColor: '#EC4899',
          badge: (kpis.anomalies?.value ?? 0) > 0 ? this.$t('org-dashboard.new-badge') : null,
          tooltip: this.$t('org-dashboard.kpi-tooltip-anomalies'),
          section: 'anomalies',
          category: 'critical',
        },
        // Rotation bornes
        ...(this.chargerRotationRate != null ? [{
          label: this.$t('org-dashboard.charger-rotation'),
          value: this.chargerRotationRate,
          icon: 'mdi-autorenew',
          iconColor: '#0EA5E9',
          unit: this.$t('org-dashboard.charger-rotation-unit'),
          tooltip: this.$t('org-dashboard.kpi-tooltip-charger-rotation'),
          category: 'usage',
        }] : []),
        // Bornes sous-utilisées
        ...(this.data?.fleetStatus?.underutilized != null ? [{
          label: this.$t('org-dashboard.underutilized-chargers'),
          value: this.data.fleetStatus.underutilized,
          icon: 'mdi-sleep',
          iconColor: '#9CA3AF',
          badge: this.$t('org-dashboard.underutilized-label', { threshold: this.data.fleetStatus.underutilizedThreshold || 5 }),
          tooltip: this.$t('org-dashboard.kpi-tooltip-underutilized', { threshold: this.data.fleetStatus.underutilizedThreshold || 5 }),
          section: 'underutilized',
          category: 'usage',
        }] : []),
        // Qualité des données
        ...(this.data?.dataQuality ? [{
          label: this.$t('org-dashboard.data-quality'),
          value: this.dataQualityData.score,
          unit: '%',
          icon: 'mdi-database-check',
          iconColor: this.dataQualityData.status === 'critical' ? '#EF4444' : this.dataQualityData.status === 'warning' ? '#F59E0B' : '#10B981',
          tooltip: this.$t('org-dashboard.kpi-tooltip-data-quality'),
          category: 'critical',
        }] : []),
        // Créneau de pointe
        ...(this.heatmapPeakSlot ? [{
          label: this.$t('org-dashboard.peak-window'),
          value: this.heatmapPeakSlot.label,
          icon: 'mdi-clock-alert-outline',
          iconColor: '#EA580C',
          badge: this.heatmapPeakSlot.sessions + ' sess.',
          tooltip: this.$t('org-dashboard.kpi-tooltip-peak-window'),
          category: 'critical',
        }] : []),
      ]
    },

    roamingDependencyPct() {
      const cb = this.data?.consumptionBreakdown
      if (!cb?.segments?.length || !cb?.total?.energy) return null
      const roamingOut = cb.segments.find(s => s.name === 'RoamingOutgoing')
      if (!roamingOut?.energy) return null
      return Math.round(roamingOut.energy / cb.total.energy * 100)
    },

    adoptionRateValue() {
      const activeUsers = this.data?.kpis?.activeUsers?.value
      const totalUsers = this.topDriversTotal?.users
      if (!activeUsers || !totalUsers) return null
      return Math.round(activeUsers / totalUsers * 100)
    },

    healthAvailabilityColor() {
      const val = this.data?.kpis?.operationalAvailability?.value
      if (val >= 95) return '#22c55e'
      if (val >= 80) return '#eab308'
      return '#ef4444'
    },

    fleetHealthData() {
      const fh = this.data?.fleetHealth
      if (!fh) return null
      // Build excluded breakdown from API or derive from bySeverity
      const excludedBreakdown = fh.excludedBreakdown || (fh.bySeverity ? {
        maintenance: fh.bySeverity.maintenance || 0,
        inactive: fh.bySeverity.inactive || 0,
      } : null)
      return {
        operationalAvailability: fh.operationalAvailability ?? 0,
        eligible: fh.eligible ?? 0,
        excluded: fh.excluded ?? 0,
        excludedBreakdown,
        bySeverity: fh.bySeverity || {},
        offlineHistogram: fh.offlineHistogram || [],
        backlogIncident: fh.backlog?.incident?.slice(0, 5) || [],
        backlogCritical: fh.backlog?.critical?.slice(0, 5) || [],
      }
    },

    fleetSummaryText() {
      const fh = this.data?.fleetHealth
      if (!fh) return null
      const total = (fh.eligible || 0) + (fh.excluded || 0)
      const active = fh.eligible || 0
      const needsAction = (fh.bySeverity?.incident || 0) + (fh.bySeverity?.critical || 0)
      const excluded = fh.excluded || 0
      return this.$t('org-dashboard.health-fleet-summary', { total, active, needsAction, excluded })
    },

    fleetEfficiencyData() {
      const fe = this.data?.fleetEfficiency
      if (!fe) return null

      // Map root-level topChargers/bottomChargers to the expected format
      const mapCharger = c => ({
        id: c.chargingPointId || c.id,
        identifier: c.name || c.identifier,
        locationName: c.location || c.locationName,
        sessions: c.sessions,
        energy: c.energy,
      })

      const topHighUsed = (this.data?.topChargers || fe.topHighUsed || []).map(mapCharger)
      const bottomRaw = this.data?.bottomChargers || []
      const topLowUsed = bottomRaw.length
        ? bottomRaw.filter(c => c.sessions > 0).map(mapCharger)
        : (fe.topLowUsed || [])
      const topNeverUsed = bottomRaw.length
        ? bottomRaw.filter(c => c.sessions === 0).map(mapCharger)
        : (fe.topNeverUsed || [])

      return {
        eligibleTotal: fe.eligibleTotal ?? 0,
        countsByUsageTier: fe.countsByUsageTier || {},
        percentByUsageTier: fe.percentByUsageTier || {},
        topHighUsed,
        topLowUsed,
        topNeverUsed,
        lowThreshold: fe.lowThreshold ?? 5,
        highThreshold: fe.highThreshold ?? 20,
      }
    },

    chargerRotationRate() {
      const sessions = this.data?.kpis?.sessions?.value
      const chargers = this.data?.fleetStatus?.total
      if (!sessions || !chargers) return null
      const periodDays = this.periodDays
      if (!periodDays) return null
      return (sessions / chargers / periodDays).toFixed(1)
    },

    periodDays() {
      const p = this.period
      if (!p) return 30
      const match = p.match(/^(\d+)d$/)
      if (match) return parseInt(match[1])
      if (p === '7d') return 7
      if (p === '30d') return 30
      if (p === '90d') return 90
      if (p === '365d' || p === '1y') return 365
      return 30
    },

    heatmapPeakInsight() {
      const s = this.heatmapPeakSlot
      if (!s) return null
      return this.$t('org-dashboard.heatmap-peak-insight', {
        pct: s.pct,
        day: s.day,
        hour: s.hour,
        hourEnd: s.hour + 1,
      })
    },

    heatmapPeakSlot() {
      const raw = this.data?.heatmap?.data
      if (!raw?.length) return null
      let peak = { count: 0, day: 0, hour: 0 }
      raw.forEach(entry => {
        if ((entry.count || 0) > peak.count) {
          peak = { count: entry.count, day: entry.day, hour: entry.hour }
        }
      })
      if (peak.count === 0) return null
      const maxCount = Math.max(...raw.map(r => r.count || 0), 1)
      const pct = Math.round((peak.count / maxCount) * 100)
      const dayNames = [
        '', this.$t('org-dashboard.day-mon'), this.$t('org-dashboard.day-tue'),
        this.$t('org-dashboard.day-wed'), this.$t('org-dashboard.day-thu'),
        this.$t('org-dashboard.day-fri'), this.$t('org-dashboard.day-sat'),
        this.$t('org-dashboard.day-sun'),
      ]
      const day = dayNames[peak.day] || ''
      return {
        pct,
        day,
        hour: peak.hour,
        sessions: peak.count,
        label: `${day} ${peak.hour}h-${peak.hour + 1}h`,
      }
    },

    totalPeriodCost() {
      const ca = this.data?.costAnalysis
      if (ca?.totalCost != null) return ca.totalCost
      if (ca?.segments?.length) {
        let total = 0
        ca.segments.forEach(s => { total += s.cost || 0 })
        return total
      }
      return null
    },

    peakLoadViewModes() {
      return [
        { value: 'sessions', label: this.$t('org-dashboard.peak-sessions') },
        { value: 'kwh', label: 'kWh' },
        { value: 'usage', label: this.$t('org-dashboard.peak-usage') },
        { value: 'errors', label: this.$t('org-dashboard.peak-errors') },
      ]
    },

    usageViewModes() {
      return [
        { value: 'sessions', label: this.$t('org-dashboard.sessions-short') },
        { value: 'kwh', label: 'kWh' },
      ]
    },

    usageChartSubtitle() {
      return this.usageMetric === 'kwh'
        ? this.$t('org-dashboard.usage-subtitle-kwh')
        : this.$t('org-dashboard.usage-subtitle')
    },

    usageChartSeries() {
      const chart = this.data?.usageChart
      if (!chart?.data?.length) return []
      if (chart.series) return chart.series

      const isKwh = this.usageMetric === 'kwh'
      const metricName = isKwh ? 'kWh' : this.$t('org-dashboard.sessions-short')
      const metricColor = isKwh ? '#059669' : '#1e40af'

      return [{
        name: this.$t('org-dashboard.occupancy') + ' (%)',
        type: 'areaspline',
        color: '#60a5fa',
        yAxis: 0,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[0, 'rgba(59, 130, 246, 0.25)'], [1, 'rgba(59, 130, 246, 0.05)']],
        },
        tooltip: { valueSuffix: ' %' },
        data: chart.data.map(d => [new Date(d.date).getTime(), Math.round((d.occupancyRate ?? d.occupancy ?? 0) * 10) / 10]),
      }, {
        name: metricName,
        type: 'spline',
        color: metricColor,
        yAxis: 1,
        tooltip: isKwh ? { valueSuffix: ' kWh' } : {},
        data: chart.data.map(d => [new Date(d.date).getTime(), isKwh ? (d.energy || 0) : (d.sessions || 0)]),
      }]
    },

    usageChartYAxes() {
      const chart = this.data?.usageChart
      if (!chart?.data?.length && !chart?.yAxes) return []
      if (chart.yAxes) return chart.yAxes

      const isKwh = this.usageMetric === 'kwh'
      return [
        { label: this.$t('org-dashboard.occupancy') + ' (%)', color: '#60a5fa' },
        { label: isKwh ? 'kWh' : this.$t('org-dashboard.sessions-short'), color: isKwh ? '#059669' : '#1e40af' },
      ]
    },

    heatmapRawData() {
      return this.data?.heatmap?.data || []
    },

    fleetStatusData() {
      const fs = this.data?.fleetStatus
      if (!fs) return { total: 0, segments: [], alert: null }
      const segments = [
        { label: this.$t('org-dashboard.fleet-available'), value: fs.available || 0, percent: fs.total ? Math.round((fs.available || 0) / fs.total * 100) : 0, color: '#10B981' },
        { label: this.$t('org-dashboard.fleet-charging'), value: fs.charging || 0, percent: fs.total ? Math.round((fs.charging || 0) / fs.total * 100) : 0, color: '#3B82F6' },
        { label: this.$t('org-dashboard.fleet-occupied'), value: fs.occupied || 0, percent: fs.total ? Math.round((fs.occupied || 0) / fs.total * 100) : 0, color: '#F59E0B' },
        { label: this.$t('org-dashboard.fleet-offline'), value: fs.offline || 0, percent: fs.total ? Math.round((fs.offline || 0) / fs.total * 100) : 0, color: '#EF4444', alert: (fs.offline || 0) > 0 },
      ]
      const criticalCount = fs.critical?.length || 0
      const alert = criticalCount > 0
        ? this.$tc('org-dashboard.critical-locations', criticalCount, { count: criticalCount })
        : null
      return { total: fs.total || 0, segments, alert, meterDevices: fs.meterDevices || 0 }
    },

    actionItemsData() {
      const fs = this.data?.fleetStatus
      if (!fs?.critical?.length) return []
      return fs.critical.slice(0, 10).map(c => ({
        severity: 'critical',
        asset: c.name,
        location: c.location,
        issue: `${c.status} - ${this.$t('org-dashboard.offline-since')} ${this.formatDate(c.offlineSince)}`,
        duration: this.formatDuration(c.offlineSince),
        actionLabel: this.$t('org-dashboard.resolve'),
        chargingPointId: c.chargingPointId,
      }))
    },

    dataQualityData() {
      const dq = this.data?.dataQuality
      if (!dq) return null

      const score = dq.scorePercent ?? dq.dataIntegrity ?? dq.overallCompleteness ?? 0
      const statusFromScore = score >= 80 ? 'good' : score >= 50 ? 'warning' : 'critical'

      // Support both old format (problematic[]) and new format (byIssueType[] + topIssues[])
      const byIssue = dq.byIssueType || []
      const issueCount = (type) => (byIssue.find(i => i.type === type) || {}).count ?? 0

      const totalProblematic = dq.totals?.problematic ?? dq.problematic?.length ?? 0

      const topProblematic = (dq.topIssues || dq.problematic || []).slice(0, 5).map(p => ({
        id: p.id ?? p.chargingPointId,
        identifier: p.identifier ?? p.name,
        locationName: p.locationName ?? p.location,
        issue: p.issueType ?? p.issue,
        duration: this.formatDuration(p.lastHeartbeat),
      }))

      return {
        score,
        status: dq.status || statusFromScore,
        breakdown: {
          heartbeatMissing: dq.heartbeatMissing ?? issueCount('no_heartbeat') + issueCount('missing_heartbeat'),
          inconsistentStatus: dq.inconsistentStatus ?? issueCount('inconsistent_status'),
          evseNoState: dq.evseNoState ?? issueCount('no_evse_state'),
        },
        totalProblematic,
        topProblematic,
      }
    },

    revenueData() {
      const rev = this.data?.revenue
      if (!rev) return { total: 0, currency: 'EUR', change: null, segments: [] }

      const segments = []
      if (rev.segments?.length) {
        // Use backend segments, excluding margin
        rev.segments
          .filter(s => !/margin/i.test(s.name || s.label || ''))
          .forEach(s => {
            segments.push({ label: s.label || s.name, percent: s.percent || 0, color: s.color || '#3B82F6' })
          })
      } else if (rev.internalPercent > 0 || rev.externalPercent > 0) {
        segments.push({ label: this.$t('org-dashboard.revenue-internal'), percent: rev.internalPercent || 0, color: '#3B82F6' })
        segments.push({ label: this.$t('org-dashboard.revenue-external'), percent: rev.externalPercent || 0, color: '#F59E0B' })
      }

      return {
        total: rev.total || 0,
        currency: rev.currency || 'EUR',
        change: rev.change ?? null,
        segments,
      }
    },


    consumptionData() {
      const cb = this.data?.consumptionBreakdown
      if (!cb) return { total: 0, totalUnit: 'kWh', segments: [] }

      const segmentColors = {
        Enterprise: '#3B82F6',
        Home: '#10B981',
        RoamingIncoming: '#8B5CF6',
        RoamingOutgoing: '#F59E0B',
      }
      const mode = this.consumptionMode

      let totalVal, totalUnit, getValue, getChange
      if (mode === 'sessions') {
        totalVal = cb.total?.sessions || 0
        totalUnit = this.$t('org-dashboard.sessions-short')
        getValue = s => s.sessions || 0
        getChange = s => s.changeSessions ?? s.change ?? 0
      } else if (mode === 'cost') {
        totalVal = (cb.segments || []).reduce((sum, s) => sum + (s.cost || 0), 0)
        totalUnit = '€'
        getValue = s => s.cost || 0
        getChange = s => s.changeCost ?? s.change ?? 0
      } else {
        totalVal = cb.total?.energy || 0
        totalUnit = 'kWh'
        getValue = s => s.energy || 0
        getChange = s => s.changeEnergy ?? s.change ?? 0
      }

      const segmentLabels = {
        Enterprise: this.$t('org-dashboard.segment-enterprise'),
        Home: this.$t('org-dashboard.segment-home'),
        RoamingIncoming: this.$t('org-dashboard.segment-roaming-incoming'),
        RoamingOutgoing: this.$t('org-dashboard.segment-roaming-outgoing'),
      }

      return {
        total: totalVal,
        totalUnit,
        segments: (cb.segments || [])
          .map(s => ({
            label: segmentLabels[s.name] || s.name,
            value: getValue(s),
            color: segmentColors[s.name] || '#6B7280',
            change: getChange(s),
          })),
      }
    },

    consumptionViewModes() {
      return [
        { value: 'kwh', label: 'kWh' },
        { value: 'sessions', label: this.$t('org-dashboard.sessions-short') },
        { value: 'cost', label: this.$t('org-dashboard.cost-label') },
      ]
    },

    costAnalysisData() {
      const ca = this.data?.costAnalysis
      const refunds = this.data?.refunds
      const segments = []

      // Home Reimb. card — from refunds block (not costAnalysis.segments)
      if (refunds) {
        const avgPerUser = refunds.memberCount > 0 ? refunds.total / refunds.memberCount : null
        const avgPerSession = refunds.sessions > 0 ? refunds.total / refunds.sessions : null
        segments.push({
          label: this.$t('org-dashboard.home-reimb'),
          amount: refunds.total || 0,
          change: refunds.total > 0 ? (refunds.change || 0) : null,
          avgPerUser: avgPerUser,
          avgPerSession: avgPerSession,
          avgCostPerKwh: refunds.avgCostPerKwh ?? null,
          color: '#3B82F6',
          icon: 'mdi-home',
          section: 'refunds',
          disabled: refunds.total === 0,
        })
      }

      // Enterprise & Roaming cards — from costAnalysis.segments (skip "Home", skip if cost === 0)
      const segmentConfig = {
        Enterprise: { color: '#3B82F6', icon: 'mdi-domain', label: this.$t('org-dashboard.enterprise-costs') },
        Home: { color: '#10B981', icon: 'mdi-home', label: this.$t('org-dashboard.home-costs') },
        RoamingIncoming: { color: '#8B5CF6', icon: 'mdi-arrow-down-bold', label: this.$t('org-dashboard.roaming-incoming-costs') },
        RoamingOutgoing: { color: '#F59E0B', icon: 'mdi-arrow-up-bold', label: this.$t('org-dashboard.roaming-outgoing-costs') },
      }
      if (ca?.segments) {
        ca.segments
          .filter(s => s.name !== 'Home' && (s.cost || 0) > 0)
          .forEach(s => {
            const cfg = segmentConfig[s.name] || { color: '#10B981', icon: 'mdi-currency-eur', label: s.name }
            segments.push({
              label: cfg.label,
              amount: s.cost || 0,
              change: s.change || 0,
              avgPerUser: s.avgPerUser || 0,
              avgPerSession: s.avgPerSession || 0,
              avgCostPerKwh: s.avgCostPerKwh ?? null,
              color: cfg.color,
              icon: cfg.icon,
            })
          })
      }

      return {
        currency: refunds?.currency || 'EUR',
        segments,
        sparklineData: (ca?.sparkline || []).map(s => s.total || 0),
        sparklineLabels: (ca?.sparkline || []).map(s => s.month || ''),
      }
    },

    costForecastData() {
      const cf = this.data?.costForecast
      if (!cf) return null
      return {
        monthlyRunRate: cf.monthlyRunRate ?? null,
        annualForecast: cf.annualForecast ?? null,
        spent: cf.spent ?? null,
        periodDays: cf.periodDays ?? 30,
        currency: cf.currency || 'EUR',
      }
    },

    ecoDriverMemberMap() {
      const map = {}
      if (this.topDrivers?.length) {
        this.topDrivers.forEach(d => {
          if (d.name && d.memberId) map[d.name] = d.memberId
        })
      }
      return map
    },

    usageSplitViewModes() {
      return [
        { value: 'kwh', label: 'kWh' },
        { value: 'sessions', label: this.$t('org-dashboard.sessions-short') },
      ]
    },

    usageSplitData() {
      const us = this.data?.usageSplit
      if (!us) return null
      const mode = this.usageSplitMode
      if (mode === 'sessions') {
        const total = (us.internalSessions || 0) + (us.externalSessions || 0)
        return {
          internalValue: us.internalSessions || 0,
          externalValue: us.externalSessions || 0,
          internalPercent: total > 0 ? Math.round((us.internalSessions || 0) / total * 100) : 0,
          externalPercent: total > 0 ? Math.round((us.externalSessions || 0) / total * 100) : 0,
          unit: this.$t('org-dashboard.sessions-short'),
        }
      }
      return {
        internalValue: us.internalKwh || 0,
        externalValue: us.externalKwh || 0,
        internalPercent: us.internalPercent || 0,
        externalPercent: us.externalPercent || 0,
        unit: 'kWh',
      }
    },

    /* fleetInventoryData is now loaded via dedicated API call — see data() + loadFleetInventory() */

    incidentsDrilldownHeaders() {
      return [
        { text: this.$t('org-dashboard.charger'), value: 'identifier' },
        { text: this.$t('org-dashboard.location'), value: 'locationName' },
        { text: this.$t('org-dashboard.incident-severity'), value: 'severity' },
        { text: this.$t('org-dashboard.incident-started'), value: 'startedAt' },
        { text: this.$t('org-dashboard.incident-duration'), value: 'durationHours' },
        { text: this.$t('org-dashboard.status'), value: 'status', sortable: false },
      ]
    },

    adoptionTrendData() {
      return this.data?.adoptionTrend || []
    },
  },

  mounted() {
    // Security: redirect if not authorized
    if (!this.checkAccess()) return

    // Apply dashboard config (ignored in ZE mode)
    if (this.dashboardConfig && !this.isZeMode) {
      if (this.dashboardConfig.filterMode) {
        this.filterMode = this.dashboardConfig.filterMode
      }
      if (this.dashboardConfig.hideAvailability) {
        this.hideAvailability = true
      }
    }

    this.loadData()
    this.loadTopDrivers()
    this.loadPeakLoad()
    this.loadEcoData()
    this.loadHealthTrend()
    this.loadIncidents()
    this.loadReliability()
    this.loadFleetInventory()
    this.loadSimUsage()
    this.loadDriverConsumption()
  },

  watch: {
    organizationId() {
      // Kept alive: ignore the param change when the user is on a sibling (e.g. /vehicles/:id) — else we
      // would fetch the whole dashboard with a vehicle id. Only reload on a real organization route.
      if (!this.isOrganizationRoute) return
      this.loadData()
      this.loadTopDrivers()
      this.loadPeakLoad()
      this.loadEcoData()
      this.loadHealthTrend()
      this.loadIncidents()
      this.loadReliability()
      this.loadFleetInventory()
      this.loadSimUsage()
      this.loadDriverConsumption()
    },
    period() {
      this.loadData()
      this.loadTopDrivers()
      this.loadPeakLoad()
      this.loadEcoData()
      this.loadHealthTrend()
      this.loadIncidents()
      this.loadReliability()
      this.loadSimUsage()
      this.loadDriverConsumption()
    },
    scope() {
      this.loadData()
      this.loadTopDrivers()
      this.loadPeakLoad()
      this.loadEcoData()
      this.loadHealthTrend()
      this.loadIncidents()
      this.loadReliability()
      this.loadFleetInventory()
      this.loadSimUsage()
      this.loadDriverConsumption()
    },
    filterMode() {
      this.loadData()
      this.loadTopDrivers()
      this.loadPeakLoad()
      this.loadEcoData()
      this.loadHealthTrend()
      this.loadIncidents()
      this.loadReliability()
      this.loadFleetInventory()
    },
    hideAvailability() {
    },
    dashboardTab(val) {
      localStorage.setItem('org_dashboard_tab', val)
    },
    peakLoadMetric() {
      this.loadPeakLoad()
    },
    driverMetric() {
      this.loadTopDrivers()
    },
  },

  methods: {
    checkAccess() {
      // ZE mode requires isZE
      if (this.$route.query.ze === '1' && !this.currentUser?.isZE) {
        this.$router.replace({ name: 'organizations.detail', params: { id: this.organizationId } })
        return false
      }
      // Normal mode requires dashboard to be enabled
      if (this.$route.query.ze !== '1' && !this.dashboardConfig?.enabled) {
        this.$router.replace({ name: 'organizations.detail', params: { id: this.organizationId } })
        return false
      }
      return true
    },

    loadData() {
      if (!this.organizationId) return

      this.loading = true
      this.error = false

      OrganizationDashboardRepository.getDashboard(this.organizationId, {
        period: this.period,
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.data = data
        })
        .catch(err => {
          this.error = true
          this.notifyError(err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    refresh() {
      this.loadData()
      this.loadTopDrivers()
      this.loadPeakLoad()
      this.loadEcoData()
      this.loadHealthTrend()
      this.loadIncidents()
      this.loadReliability()
      this.loadDriverConsumption()
    },

    loadPeakLoad() {
      if (!this.organizationId) return
      this.peakLoadLoading = true
      OrganizationDashboardRepository.getPeakLoad(this.organizationId, {
        period: this.period,
        scope: this.scope,
        metric: this.peakLoadMetric,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.peakLoadSlots = data.slots || data.data || []
        })
        .catch(() => {
          this.peakLoadSlots = []
        })
        .finally(() => {
          this.peakLoadLoading = false
        })
    },

    openChargerDrawer(chargingPointId) {
      if (!chargingPointId) return
      this.chargerDrawer.open = true
      this.chargerDrawer.chargingPointId = chargingPointId
      this.chargerDrawer.data = null
      this.chargerDrawer.loading = true
      OrganizationDashboardRepository.getChargerDetails(this.organizationId, chargingPointId, {
        period: this.period,
        scope: this.scope,
      })
        .then(data => {
          this.chargerDrawer.data = data
        })
        .catch(err => {
          this.notifyError(err)
          this.chargerDrawer.open = false
        })
        .finally(() => {
          this.chargerDrawer.loading = false
        })
    },

    loadEcoData() {
      if (!this.organizationId) return
      this.ecoLoading = true
      OrganizationDashboardRepository.getEcoImpact(this.organizationId, {
        period: this.period,
        scope: this.scope,
      })
        .then(data => {
          this.ecoData = data
        })
        .catch(() => {
          this.ecoData = null
        })
        .finally(() => {
          this.ecoLoading = false
        })
    },

    loadHealthTrend() {
      if (!this.organizationId) return
      this.healthTrendLoading = true
      OrganizationDashboardRepository.getHealthTrend(this.organizationId, {
        period: this.period,
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.healthTrendData = data.data || data || []
        })
        .catch(() => {
          this.healthTrendData = []
        })
        .finally(() => {
          this.healthTrendLoading = false
        })
    },

    loadIncidents() {
      if (!this.organizationId) return
      this.incidentsLoading = true
      OrganizationDashboardRepository.getIncidents(this.organizationId, {
        period: this.period,
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
        limit: 10,
      })
        .then(data => {
          this.incidents = data.data || []
          this.incidentsSummary = data.summary || null
        })
        .catch(() => {
          this.incidents = []
          this.incidentsSummary = null
        })
        .finally(() => {
          this.incidentsLoading = false
        })
    },

    openIncidentsDrilldown() {
      this.incidentsDrilldown.open = true
      this.incidentsDrilldown.options = { page: 1, itemsPerPage: 20 }
      this.loadIncidentsDrilldownPage(this.incidentsDrilldown.options)
    },

    loadIncidentsDrilldownPage(options) {
      this.incidentsDrilldown.loading = true
      OrganizationDashboardRepository.getIncidents(this.organizationId, {
        period: this.period,
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
        page: options.page || 1,
        limit: options.itemsPerPage || 20,
      })
        .then(data => {
          this.incidentsDrilldown.items = (data.data || []).map(item => ({
            ...item,
            status: item.resolvedAt ? 'resolved' : 'open',
          }))
          this.incidentsDrilldown.totalItems = data.totalItems || (data.summary && data.summary.totalIncidents) || 0
        })
        .catch(() => {
          this.incidentsDrilldown.items = []
          this.incidentsDrilldown.totalItems = 0
        })
        .finally(() => {
          this.incidentsDrilldown.loading = false
        })
    },

    formatIncidentDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatIncidentDuration(hours) {
      const totalMinutes = Math.round(hours * 60)
      const d = Math.floor(totalMinutes / 1440)
      const h = Math.floor((totalMinutes % 1440) / 60)
      const m = totalMinutes % 60
      if (d > 0) return (h > 0 ? `${d}j ${h}h` : `${d}j`) + (m > 0 ? ` ${m}min` : '')
      if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`
      return `${m}min`
    },

    loadReliability() {
      if (!this.organizationId) return
      this.reliabilityLoading = true
      OrganizationDashboardRepository.getReliability(this.organizationId, {
        period: this.period,
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.reliabilityData = data
        })
        .catch(() => {
          this.reliabilityData = null
        })
        .finally(() => {
          this.reliabilityLoading = false
        })
    },

    loadFleetInventory() {
      if (!this.organizationId) return
      OrganizationDashboardRepository.getFleetInventory(this.organizationId, {
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.fleetInventoryData = data
        })
        .catch(() => {
          this.fleetInventoryData = null
        })
    },

    loadSimUsage() {
      if (!this.organizationId) return
      this.simUsageLoading = true
      OrganizationDashboardRepository.getSimUsage(this.organizationId, {
        period: this.period,
        scope: this.scope,
      })
        .then(data => { this.simUsageData = data })
        .catch(() => { this.simUsageData = null })
        .finally(() => { this.simUsageLoading = false })
    },

    openMemberDrawer(memberId) {
      if (!memberId) return
      this.memberDrawer.open = true
      this.memberDrawer.memberId = memberId
      this.memberDrawer.data = null
      this.memberDrawer.loading = true
      OrganizationDashboardRepository.getMemberDetail(this.organizationId, memberId, {
        period: this.period,
        scope: this.scope,
      })
        .then(data => {
          this.memberDrawer.data = data
        })
        .catch(err => {
          this.notifyError(err)
          this.memberDrawer.open = false
        })
        .finally(() => {
          this.memberDrawer.loading = false
        })
    },

    loadDriverConsumption() {
      if (!this.organizationId) return
      this.driverConsumptionLoading = true
      Promise.all([
        OrganizationDashboardRepository.getDriverConsumptionTimeline(this.organizationId, {
          period: this.period,
          scope: this.scope,
        }),
        OrganizationDashboardRepository.getDriverConsumptionBreakdown(this.organizationId, {
          period: this.period,
          scope: this.scope,
        }),
      ])
        .then(([timeline, breakdown]) => {
          this.driverConsumptionTimeline = timeline.data?.data || []
          this.driverConsumptionInterval = timeline.data?.interval || 'day'
          this.driverConsumptionBreakdown = breakdown.data || null
        })
        .catch(() => {
          this.driverConsumptionTimeline = []
          this.driverConsumptionBreakdown = null
        })
        .finally(() => {
          this.driverConsumptionLoading = false
        })
    },

    loadTopDrivers() {
      if (!this.organizationId) return
      this.topDriversLoading = true
      OrganizationDashboardRepository.getTopDrivers(this.organizationId, {
        period: this.period,
        scope: this.scope,
        sortBy: this.driverMetric || 'energy',
        segment: this.driverSegment,
        limit: 10000,
      })
        .then(data => {
          const allDrivers = data.data || data.drivers || []
          this.topDrivers = allDrivers
          this.topDriversTotal = data.total || null
        })
        .catch(() => {
          this.topDrivers = []
        })
        .finally(() => {
          this.topDriversLoading = false
        })
    },

    getDefaultSort(section) {
      const sortMap = {
        sessions: { sortBy: ['energy'], sortDesc: [true] },
        energy: { sortBy: ['energy'], sortDesc: [true] },
        activeUsers: { sortBy: ['energy'], sortDesc: [true] },
        chargers: { sortBy: ['energy'], sortDesc: [true] },
        revenue: { sortBy: ['revenue'], sortDesc: [true] },
        costs: { sortBy: ['cost'], sortDesc: [true] },
        refunds: { sortBy: ['amount'], sortDesc: [true] },
        offline: { sortBy: [], sortDesc: [] },
        underutilized: { sortBy: ['sessions'], sortDesc: [false] },
        anomalies: { sortBy: [], sortDesc: [] },
      }
      return sortMap[section] || { sortBy: [], sortDesc: [] }
    },

    openDetail(section, title) {
      // "Nécessitent action" KPI — open health drilldown on first non-empty severity
      if (section === 'offline' && this.data?.fleetHealth) {
        const bySeverity = this.data.fleetHealth.bySeverity || {}
        const cat = (bySeverity.incident || 0) > 0 ? 'incident' : 'critical'
        this.openHealthDrilldown(cat)
        return
      }
      // Eco section — switch to first tab then scroll to block
      if (section === 'eco') {
        if (!this.ecoData) this.loadEcoData()
        if (this.dashboardTab !== 0) {
          this.dashboardTab = 0
          this.$nextTick(() => {
            setTimeout(() => {
              const el = document.getElementById('eco-impact-section')
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 250)
          })
        } else {
          const el = document.getElementById('eco-impact-section')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        return
      }
      this.detailDialog.section = section
      this.detailDialog.title = title
      this.detailDialog.headers = this.getDetailHeaders(section)
      this.detailDialog.items = []
      this.detailDialog.totalItems = 0
      const defaultSort = this.getDefaultSort(section)
      this.detailDialog.options = { page: 1, itemsPerPage: 10, ...defaultSort }
      this.detailDialog.loading = false
      this.detailDialog.kpiCards = this.getDetailKpiCards(section)
      this.detailDialog.open = true
      this.$nextTick(() => {
        this.loadDetailPage(this.detailDialog.options)
      })
    },

    getDetailKpiCards(section) {
      if (section === 'underutilized') {
        const fs = this.data?.fleetStatus
        const threshold = fs?.underutilizedThreshold || 5
        return [
          { label: this.$t('org-dashboard.underutilized-chargers'), value: fs?.underutilized || 0, icon: 'mdi-sleep', color: '#9CA3AF' },
          { label: 'Total', value: fs?.total || 0, icon: 'mdi-ev-station', color: '#6B7280' },
          { label: this.$t('org-dashboard.underutilized-threshold-label'), value: '< ' + threshold + ' sess.', icon: 'mdi-filter-outline', color: '#F59E0B' },
        ]
      }
      if (section === 'fleet') {
        const fs = this.data?.fleetStatus
        if (!fs) return []
        return [
          { label: 'Total', value: fs.total || 0, icon: 'mdi-ev-station', color: '#6B7280' },
          { label: this.$t('org-dashboard.fleet-available'), value: fs.available || 0, icon: 'mdi-check-circle', color: '#10B981' },
          { label: this.$t('org-dashboard.fleet-charging'), value: fs.charging || 0, icon: 'mdi-flash', color: '#3B82F6' },
          { label: this.$t('org-dashboard.fleet-offline'), value: fs.offline || 0, icon: 'mdi-alert-circle', color: '#EF4444' },
        ]
      }
      if (section === 'costs') {
        const ca = this.data?.costAnalysis
        if (!ca) return []
        const totalCost = (ca.segments || []).reduce((sum, s) => sum + (s.cost || 0), 0)
        return [
          { label: this.$t('org-dashboard.total-cost'), value: this.formatCurrencyValue(totalCost, 'EUR'), icon: 'mdi-cash-multiple', color: '#3B82F6' },
          { label: this.$t('org-dashboard.active-users'), value: this.data?.kpis?.activeUsers?.value || '-', icon: 'mdi-account-group', color: '#8B5CF6' },
        ]
      }
      if (section === 'refunds') {
        const ref = this.data?.refunds
        if (!ref) return []
        return [
          { label: this.$t('org-dashboard.refunds-amount'), value: this.formatCurrencyValue(ref.total || 0, ref.currency || 'EUR'), icon: 'mdi-cash', color: '#3B82F6' },
          { label: this.$t('org-dashboard.refunds-energy'), value: (ref.energy || 0) + ' kWh', icon: 'mdi-flash', color: '#F59E0B' },
          { label: this.$t('org-dashboard.refunds-sessions'), value: ref.sessions || 0, icon: 'mdi-ev-station', color: '#10B981' },
          { label: this.$t('org-dashboard.refunds-members'), value: ref.memberCount || 0, icon: 'mdi-account-group', color: '#8B5CF6' },
        ]
      }
      if (section === 'sessions' || section === 'energy') {
        const kpis = this.data?.kpis
        if (!kpis) return []
        return [
          { label: this.$t('org-dashboard.sessions-short'), value: (kpis.sessions?.value || 0).toLocaleString(), icon: 'mdi-ev-station', color: '#3B82F6' },
          { label: this.$t('org-dashboard.energy'), value: Math.round(kpis.energy?.value || 0).toLocaleString() + ' kWh', icon: 'mdi-flash', color: '#F59E0B' },
          { label: this.$t('org-dashboard.chargers-count'), value: this.data?.fleetStatus?.total || '-', icon: 'mdi-ev-plug-type2', color: '#0EA5E9' },
        ]
      }
      if (section === 'activeUsers') {
        const kpis = this.data?.kpis
        if (!kpis) return []
        return [
          { label: this.$t('org-dashboard.active-users'), value: (kpis.activeUsers?.value || 0).toLocaleString(), icon: 'mdi-account-group', color: '#8B5CF6' },
          { label: this.$t('org-dashboard.sessions-short'), value: (kpis.sessions?.value || 0).toLocaleString(), icon: 'mdi-ev-station', color: '#3B82F6' },
          { label: this.$t('org-dashboard.energy'), value: Math.round(kpis.energy?.value || 0).toLocaleString() + ' kWh', icon: 'mdi-flash', color: '#F59E0B' },
        ]
      }
      return []
    },

    loadDetailPage(options) {
      if (!this.detailDialog.open) return
      if (this.detailDialog.loading) return
      this.detailDialog.options = options

      const sortBy = options.sortBy && options.sortBy.length ? options.sortBy[0] : null
      const sortDir = sortBy && options.sortDesc && options.sortDesc[0] ? 'desc' : sortBy ? 'asc' : null

      // Fleet sections (all chargers / offline / underutilized) use the dedicated fleet endpoint
      if (this.detailDialog.section === 'fleet' || this.detailDialog.section === 'offline' || this.detailDialog.section === 'underutilized') {
        const status = this.detailDialog.section === 'offline' ? 'OFFLINE' : this.detailDialog.section === 'underutilized' ? 'UNDERUTILIZED' : 'all'
        this.detailDialog.loading = true
        OrganizationDashboardRepository.getFleetDetail(
          this.organizationId,
          {
            period: this.period,
            scope: this.scope,
            status,
            page: options.page,
            limit: options.itemsPerPage,
            sortBy,
            sortDir,
            filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
          }
        )
          .then(data => {
            const items = (data.data || []).map(c => {
              // Handle MeterDevice items
              if (c.isMeterDevice) {
                const loc = c.location || (c.address
                  ? [c.address.number, c.address.street, c.address.zipcode, c.address.city].filter(Boolean).join(' ')
                  : '-')
                return {
                  ...c,
                  chargingPointId: null,
                  location: loc,
                  status: null,
                  lastConnection: '-',
                  _isMeterDevice: true,
                }
              }
              // Derive status from EVSEs if not at top level
              let status = c.status
              if (!status && c.evses && c.evses.length) {
                const priorities = ['OUTOFORDER', 'FAULTED', 'OFFLINE', 'BLOCKED', 'MAINTENANCE', 'UNKNOWN', 'OCCUPIED', 'CHARGING', 'AVAILABLE']
                status = c.evses.reduce((worst, evse) => {
                  const wi = priorities.indexOf(worst)
                  const ei = priorities.indexOf(evse.status)
                  return ei < wi && ei >= 0 ? evse.status : worst
                }, c.evses[0].status || 'UNKNOWN')
              }
              if (!status) status = c.connected === false ? 'OFFLINE' : 'UNKNOWN'
              return {
                ...c,
                status,
                lastConnection: c.lastConnection ? this.formatDate(c.lastConnection) : '-',
              }
            })
            this.detailDialog.items = items
            this.detailDialog.totalItems = data.pagination?.total || 0
            // Update KPI cards from fleet summary if available
            const s = data.summary || data
            if (s.total != null) {
              this.detailDialog.kpiCards = [
                { label: 'Total', value: s.total || 0, icon: 'mdi-ev-station', color: '#6B7280' },
                { label: this.$t('org-dashboard.fleet-available'), value: s.available || 0, icon: 'mdi-check-circle', color: '#10B981' },
                { label: this.$t('org-dashboard.fleet-charging'), value: s.charging || 0, icon: 'mdi-flash', color: '#3B82F6' },
                { label: this.$t('org-dashboard.fleet-offline'), value: s.offline || 0, icon: 'mdi-alert-circle', color: '#EF4444' },
              ]
            }
          })
          .catch(err => {
            this.detailDialog.items = []
            this.detailDialog.totalItems = 0
            this.notifyError(err)
          })
          .finally(() => {
            this.detailDialog.loading = false
          })
        return
      }

      this.detailDialog.loading = true
      OrganizationDashboardRepository.getDashboardDetail(
        this.organizationId,
        this.detailDialog.section,
        {
          period: this.period,
          scope: this.scope,
          page: options.page,
          limit: options.itemsPerPage,
          sortBy,
          sortDir,
          filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
        }
      )
        .then(data => {
          let items = data.data || data.items || []

          // Format anomalies rows — API uses asset/assetId instead of name/chargingPointId
          if (this.detailDialog.section === 'anomalies') {
            items = items.map(row => ({
              ...row,
              name: row.asset || row.name,
              chargingPointId: row.assetId || row.chargingPointId,
            }))
          }

          // Update sessions/energy KPI cards with fresh API totals
          if ((this.detailDialog.section === 'sessions' || this.detailDialog.section === 'energy') && data.total) {
            this.detailDialog.kpiCards = [
              { label: this.$t('org-dashboard.sessions-short'), value: (data.total.sessions || 0).toLocaleString(), icon: 'mdi-ev-station', color: '#3B82F6' },
              { label: this.$t('org-dashboard.energy'), value: Math.round(data.total.energy || 0).toLocaleString() + ' kWh', icon: 'mdi-flash', color: '#F59E0B' },
              { label: this.$t('org-dashboard.chargers-count'), value: data.total.chargers || data.pagination?.total || '-', icon: 'mdi-ev-plug-type2', color: '#0EA5E9' },
            ]
          }

          // Update activeUsers KPI cards with fresh API totals
          if (this.detailDialog.section === 'activeUsers' && data.total) {
            this.detailDialog.kpiCards = [
              { label: this.$t('org-dashboard.active-users'), value: (data.total.users || data.pagination?.total || 0).toLocaleString(), icon: 'mdi-account-group', color: '#8B5CF6' },
              { label: this.$t('org-dashboard.energy'), value: Math.round(data.total.energy || 0).toLocaleString() + ' kWh', icon: 'mdi-flash', color: '#F59E0B' },
              { label: this.$t('org-dashboard.cost-column'), value: data.total.cost != null ? this.formatCurrencyValue(data.total.cost, 'EUR') : '-', icon: 'mdi-cash', color: '#10B981' },
            ]
          }

          // Format costs rows
          if (this.detailDialog.section === 'costs') {
            items = items.map(row => ({
              ...row,
              energy: row.energy != null ? row.energy.toLocaleString() : '-',
              cost: row.cost != null ? this.formatCurrencyValue(row.cost, 'EUR') : '-',
              avgCost: row.avgCost != null ? this.formatCurrencyValue(row.avgCost, 'EUR') : '-',
            }))
            // Update KPI cards from detail total if available
            if (data.total) {
              this.detailDialog.kpiCards = [
                { label: this.$t('org-dashboard.total-cost'), value: this.formatCurrencyValue(data.total.cost || 0, 'EUR'), icon: 'mdi-cash-multiple', color: '#DC2626' },
                { label: this.$t('org-dashboard.active-users'), value: (data.total.users || data.pagination?.total || 0).toLocaleString(), icon: 'mdi-account-group', color: '#8B5CF6' },
              ]
            }
          }

          // Format refunds rows
          if (this.detailDialog.section === 'refunds') {
            const currency = data.total?.currency || this.data?.refunds?.currency || 'EUR'
            items = items.map(row => ({
              ...row,
              energy: (row.energy || 0) + ' kWh',
              amount: this.formatCurrencyValue(row.amount, currency),
              avgPerSession: this.formatCurrencyValue(row.avgPerSession, currency),
            }))
            // Update KPI cards from detail total if available
            if (data.total) {
              this.detailDialog.kpiCards = [
                { label: this.$t('org-dashboard.refunds-amount'), value: this.formatCurrencyValue(data.total.amount || 0, currency), icon: 'mdi-cash', color: '#3B82F6' },
                { label: this.$t('org-dashboard.refunds-energy'), value: (data.total.energy || 0) + ' kWh', icon: 'mdi-flash', color: '#F59E0B' },
                { label: this.$t('org-dashboard.refunds-sessions'), value: data.total.sessions || 0, icon: 'mdi-ev-station', color: '#10B981' },
                { label: this.$t('org-dashboard.refunds-members'), value: data.total.members || 0, icon: 'mdi-account-group', color: '#8B5CF6' },
              ]
            }
          }

          this.detailDialog.items = items
          this.detailDialog.totalItems = (data.pagination ? data.pagination.total : null) || data.total || 0
          // Ensure totalItems is a number
          if (typeof this.detailDialog.totalItems !== 'number') {
            this.detailDialog.totalItems = this.detailDialog.items.length
          }
        })
        .catch(err => {
          this.detailDialog.items = []
          this.detailDialog.totalItems = 0
          this.notifyError(err)
        })
        .finally(() => {
          this.detailDialog.loading = false
        })
    },

    getDetailHeaders(section) {
      const defaultHeaders = [
        { text: 'ID', value: 'id' },
        { text: this.$t('org-dashboard.name'), value: 'name' },
        { text: this.$t('org-dashboard.value'), value: 'value' },
        { text: this.$t('org-dashboard.date'), value: 'date' },
      ]

      const headersMap = {
        fleet: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.status'), value: 'status', chipStatus: true },
          { text: this.$t('org-dashboard.last-seen'), value: 'lastConnection' },
        ],
        offline: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.status'), value: 'status', chipStatus: true },
          { text: this.$t('org-dashboard.last-seen'), value: 'lastConnection' },
        ],
        underutilized: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.status'), value: 'status', chipStatus: true },
        ],
        anomalies: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.anomaly-issue'), value: 'issue' },
          { text: this.$t('org-dashboard.anomaly-duration'), value: 'duration' },
        ],
        activeUsers: [
          { text: this.$t('org-dashboard.name'), value: 'name', linkType: 'member', linkId: 'memberId' },
          { text: 'Email', value: 'email' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.cost-column'), value: 'cost' },
        ],
        sessions: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.revenue') + ' (€)', value: 'revenue' },
        ],
        energy: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.revenue') + ' (€)', value: 'revenue' },
        ],
        availability: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.availability-label'), value: 'availability' },
          { text: this.$t('org-dashboard.status'), value: 'status', chipStatus: true },
        ],
        revenue: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.revenue') + ' (€)', value: 'revenue' },
          { text: this.$t('org-dashboard.margin') + ' (€)', value: 'margin' },
        ],
        chargers: [
          { text: this.$t('org-dashboard.charger'), value: 'name', linkType: 'chargingPoint', linkId: 'chargingPointId' },
          { text: this.$t('org-dashboard.location'), value: 'location' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.revenue') + ' (€)', value: 'revenue' },
        ],
        costs: [
          { text: this.$t('org-dashboard.name'), value: 'name' },
          { text: this.$t('org-dashboard.company'), value: 'company' },
          { text: this.$t('org-dashboard.sessions-short'), value: 'sessions' },
          { text: this.$t('org-dashboard.energy') + ' (kWh)', value: 'energy' },
          { text: this.$t('org-dashboard.cost-column'), value: 'cost' },
          { text: this.$t('org-dashboard.avg-cost'), value: 'avgCost' },
        ],
        refunds: [
          { text: this.$t('org-dashboard.refunds-member'), value: 'name' },
          { text: this.$t('org-dashboard.refunds-sessions'), value: 'sessions' },
          { text: this.$t('org-dashboard.refunds-energy'), value: 'energy' },
          { text: this.$t('org-dashboard.refunds-amount'), value: 'amount' },
          { text: this.$t('org-dashboard.refunds-avg-session'), value: 'avgPerSession' },
        ],
      }

      return headersMap[section] || defaultHeaders
    },

    onCostCardClick(section) {
      if (section === 'refunds') {
        this.openDetail('refunds', this.$t('org-dashboard.refunds-title'))
      }
    },

    handleAction(item) {
      if (item.chargingPointId) {
        this.openChargerDrawer(item.chargingPointId)
      }
    },

    openHealthDrilldown(category) {
      this.healthDrilldown.category = category || 'incident'
      this.healthDrilldown.items = []
      this.healthDrilldown.totalItems = 0
      this.healthDrilldown.options = { page: 1, itemsPerPage: 20 }
      this.healthDrilldown.kpiCards = this.getHealthDrilldownKpiCards()
      this.healthDrilldown.open = true
      this.$nextTick(() => {
        this.loadHealthDrilldownPage(this.healthDrilldown.options)
      })
    },

    onHealthCategoryChange(category) {
      this.healthDrilldown.category = category
      this.healthDrilldown.options = { page: 1, itemsPerPage: this.healthDrilldown.options.itemsPerPage || 20 }
      this.loadHealthDrilldownPage(this.healthDrilldown.options)
    },

    loadHealthDrilldownPage(options) {
      if (!this.healthDrilldown.open) return
      this.healthDrilldown.options = options
      this.healthDrilldown.loading = true
      OrganizationDashboardRepository.getHealthDrilldown(this.organizationId, {
        category: this.healthDrilldown.category,
        scope: this.scope,
        page: options.page,
        limit: options.itemsPerPage,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.healthDrilldown.items = data.data || []
          this.healthDrilldown.totalItems = data.pagination?.total || 0
        })
        .catch(err => {
          this.healthDrilldown.items = []
          this.healthDrilldown.totalItems = 0
          this.notifyError(err)
        })
        .finally(() => {
          this.healthDrilldown.loading = false
        })
    },

    getHealthDrilldownKpiCards() {
      const fh = this.data?.fleetHealth
      if (!fh) return []
      return [
        { label: this.$t('org-dashboard.health-availability-label'), value: (fh.operationalAvailability ?? 0) + '%', icon: 'mdi-shield-check', color: this.healthAvailabilityColor },
        { label: this.$t('org-dashboard.health-eligible'), value: fh.eligible || 0, icon: 'mdi-check-circle', color: '#22c55e' },
        { label: this.$t('org-dashboard.health-incident'), value: fh.bySeverity?.incident || 0, icon: 'mdi-alert-circle', color: '#f97316' },
        { label: this.$t('org-dashboard.health-critical'), value: fh.bySeverity?.critical || 0, icon: 'mdi-clock', color: '#ef4444' },
      ]
    },

    openEfficiencyDrilldown(tier) {
      this.efficiencyDrilldown.tier = tier || 'low'
      this.efficiencyDrilldown.items = []
      this.efficiencyDrilldown.totalItems = 0
      this.efficiencyDrilldown.options = { page: 1, itemsPerPage: 20 }
      this.efficiencyDrilldown.open = true
      this.$nextTick(() => {
        this.loadEfficiencyDrilldownPage(this.efficiencyDrilldown.options)
      })
    },

    onEfficiencyTierChange(tier) {
      this.efficiencyDrilldown.tier = tier
      this.efficiencyDrilldown.options = { page: 1, itemsPerPage: this.efficiencyDrilldown.options.itemsPerPage || 20 }
      this.loadEfficiencyDrilldownPage(this.efficiencyDrilldown.options)
    },

    loadEfficiencyDrilldownPage(options) {
      if (!this.efficiencyDrilldown.open) return
      this.efficiencyDrilldown.options = options
      this.efficiencyDrilldown.loading = true
      OrganizationDashboardRepository.getEfficiencyDrilldown(this.organizationId, {
        period: this.period,
        scope: this.scope,
        tier: this.efficiencyDrilldown.tier,
        page: options.page,
        limit: options.itemsPerPage,
        filterMode: 'all',
      })
        .then(data => {
          this.efficiencyDrilldown.items = data.data || []
          this.efficiencyDrilldown.totalItems = data.pagination?.total || 0
        })
        .catch(err => {
          this.efficiencyDrilldown.items = []
          this.efficiencyDrilldown.totalItems = 0
          this.notifyError(err)
        })
        .finally(() => {
          this.efficiencyDrilldown.loading = false
        })
    },

    openFirmwareDrilldown({ vendor, model, version, status, versions } = {}) {
      this.firmwareDrilldown.vendor = vendor || null
      this.firmwareDrilldown.model = model || null
      this.firmwareDrilldown.version = version || null
      this.firmwareDrilldown.status = status || 'all'
      if (versions?.length) {
        const total = versions.reduce((s, v) => s + (v.count || 0), 0)
        const upToDate = versions.filter(v => v.isLatest).reduce((s, v) => s + (v.count || 0), 0)
        this.firmwareDrilldown.summary = { total, upToDate, outdated: total - upToDate, versions }
      } else {
        this.firmwareDrilldown.summary = null
      }
      this.firmwareDrilldown.items = []
      this.firmwareDrilldown.totalItems = 0
      this.firmwareDrilldown.options = { page: 1, itemsPerPage: 20 }
      this.firmwareDrilldown.open = true
      this.$nextTick(() => {
        this.loadFirmwareDrilldownPage(this.firmwareDrilldown.options)
      })
    },

    onFirmwareVersionChange(version) {
      this.firmwareDrilldown.version = version
      this.firmwareDrilldown.options = { page: 1, itemsPerPage: this.firmwareDrilldown.options.itemsPerPage || 20 }
      this.loadFirmwareDrilldownPage(this.firmwareDrilldown.options)
    },

    loadFirmwareDrilldownPage(options) {
      if (!this.firmwareDrilldown.open) return
      this.firmwareDrilldown.options = options
      this.firmwareDrilldown.loading = true
      OrganizationDashboardRepository.getFirmwareDrilldown(this.organizationId, {
        scope: this.scope,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
        vendor: this.firmwareDrilldown.vendor,
        model: this.firmwareDrilldown.model,
        version: this.firmwareDrilldown.version,
        status: this.firmwareDrilldown.status,
        page: options.page,
        limit: options.itemsPerPage,
        sortBy: options.sortBy?.[0],
        sortDir: options.sortDesc?.[0] ? 'desc' : 'asc',
      })
        .then(data => {
          this.firmwareDrilldown.items = data.data || []
          this.firmwareDrilldown.totalItems = data.pagination?.total || 0
          if (data.summary) {
            const preservedVersions = this.firmwareDrilldown.summary?.versions
            this.firmwareDrilldown.summary = data.summary
            if (preservedVersions?.length) {
              this.firmwareDrilldown.summary.versions = preservedVersions
            }
          }
        })
        .catch(err => {
          this.firmwareDrilldown.items = []
          this.firmwareDrilldown.totalItems = 0
          this.notifyError(err)
        })
        .finally(() => {
          this.firmwareDrilldown.loading = false
        })
    },

    openSimDrilldown({ type, bucket } = {}) {
      this.simDrilldown.simType = type || 'connected'
      this.simDrilldown.bucket = bucket || null
      this.simDrilldown.summary = null
      this.simDrilldown.items = []
      this.simDrilldown.totalItems = 0
      this.simDrilldown.options = { page: 1, itemsPerPage: 20 }
      this.simDrilldown.open = true
      this.$nextTick(() => {
        this.loadSimDrilldownPage(this.simDrilldown.options)
      })
    },

    onSimTypeChange(type) {
      this.simDrilldown.simType = type
      this.simDrilldown.bucket = null
      this.simDrilldown.options = { page: 1, itemsPerPage: this.simDrilldown.options.itemsPerPage || 20 }
      this.loadSimDrilldownPage(this.simDrilldown.options)
    },

    loadSimDrilldownPage(options) {
      if (!this.simDrilldown.open) return
      this.simDrilldown.options = options
      this.simDrilldown.loading = true
      OrganizationDashboardRepository.getSimDrilldown(this.organizationId, {
        scope: this.scope,
        period: this.period,
        type: this.simDrilldown.simType,
        bucket: this.simDrilldown.bucket,
        page: options.page,
        limit: options.itemsPerPage,
        sortBy: options.sortBy?.[0],
        sortDir: options.sortDesc?.[0] ? 'desc' : 'asc',
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.simDrilldown.items = data.data || []
          this.simDrilldown.totalItems = data.pagination?.total || 0
          if (data.summary) this.simDrilldown.summary = data.summary
        })
        .catch(err => {
          this.simDrilldown.items = []
          this.simDrilldown.totalItems = 0
          this.notifyError(err)
        })
        .finally(() => {
          this.simDrilldown.loading = false
        })
    },

    openDataQualityDrilldown() {
      this.dataQualityDrilldown.items = []
      this.dataQualityDrilldown.totalItems = 0
      this.dataQualityDrilldown.options = { page: 1, itemsPerPage: 20 }
      this.dataQualityDrilldown.open = true
      this.$nextTick(() => {
        this.loadDataQualityDrilldownPage(this.dataQualityDrilldown.options)
      })
    },

    loadDataQualityDrilldownPage(options) {
      if (!this.dataQualityDrilldown.open) return
      this.dataQualityDrilldown.options = options
      this.dataQualityDrilldown.loading = true
      OrganizationDashboardRepository.getDataQualityDrilldown(this.organizationId, {
        period: this.period,
        scope: this.scope,
        page: options.page,
        limit: options.itemsPerPage,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          this.dataQualityDrilldown.items = data.data || []
          this.dataQualityDrilldown.totalItems = data.pagination?.total || 0
        })
        .catch(err => {
          this.dataQualityDrilldown.items = []
          this.dataQualityDrilldown.totalItems = 0
          this.notifyError(err)
        })
        .finally(() => {
          this.dataQualityDrilldown.loading = false
        })
    },

    openEcoDriversDrilldown() {
      this.ecoDriversDrilldown.items = []
      this.ecoDriversDrilldown.totalItems = 0
      this.ecoDriversDrilldown.totals = null
      this.ecoDriversDrilldown.options = { page: 1, itemsPerPage: 20, sortBy: ['co2SavedKg'], sortDesc: [true] }
      this.ecoDriversDrilldown.open = true
      this.$nextTick(() => {
        this.loadEcoDriversDrilldownPage(this.ecoDriversDrilldown.options)
      })
    },

    loadEcoDriversDrilldownPage(options) {
      if (!this.ecoDriversDrilldown.open) return
      this.ecoDriversDrilldown.options = options
      this.ecoDriversDrilldown.loading = true

      const sortBy = options.sortBy?.[0] || 'co2SavedKg'
      const sortDir = options.sortDesc?.[0] ? 'desc' : 'asc'
      // CO2 is proportional to energy, so sort by energy for co2
      const apiSortBy = sortBy === 'co2SavedKg' ? 'co2' : sortBy

      OrganizationDashboardRepository.getTopDrivers(this.organizationId, {
        period: this.period,
        scope: this.scope,
        sortBy: apiSortBy,
        sortDir,
        page: options.page,
        limit: options.itemsPerPage,
        filterMode: this.filterMode !== 'active-only' ? this.filterMode : undefined,
      })
        .then(data => {
          const items = (data.data || data.drivers || []).map((d, i) => ({
            ...d,
            rank: (options.page - 1) * options.itemsPerPage + i + 1,
          }))
          this.ecoDriversDrilldown.items = items
          this.ecoDriversDrilldown.totalItems = data.total?.users || data.pagination?.total || items.length
          this.ecoDriversDrilldown.totals = data.total || null
        })
        .catch(() => {
          this.ecoDriversDrilldown.items = []
          this.ecoDriversDrilldown.totalItems = 0
        })
        .finally(() => {
          this.ecoDriversDrilldown.loading = false
        })
    },

    onDetailLinkClick({ item, linkType, linkId }) {
      if (!linkId) return
      if (linkType === 'chargingPoint' && !item._isMeterDevice) {
        this.openChargerDrawer(linkId)
      } else if (linkType === 'member') {
        this.openMemberDrawer(linkId)
      }
    },

    formatCurrencyValue(val, currency = 'EUR') {
      if (val === null || val === undefined) return '--'
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: currency,
      }).format(val)
    },

    formatLargeNumber(val) {
      if (val >= 1000000) return Math.round(val / 1000000) + 'M'
      if (val >= 1000) return Math.round(val / 1000) + 'k'
      return val
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(this.$i18n.locale, { day: '2-digit', month: 'short', year: 'numeric' })
    },

    formatDuration(sinceStr) {
      if (!sinceStr) return '-'
      const ms = Date.now() - new Date(sinceStr).getTime()
      const days = Math.floor(ms / 86400000)
      const hours = Math.floor((ms % 86400000) / 3600000)
      if (days > 0) return `${days}d ${hours}h`
      const mins = Math.floor((ms % 3600000) / 60000)
      return `${hours}h ${mins}m`
    },

    formatDurationShort(seconds) {
      if (!seconds && seconds !== 0) return '-'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      if (h > 0) return `${h}h${m > 0 ? m + 'm' : ''}`
      return `${m}m`
    },

    formatPeakSaturationSlot(ps) {
      if (!ps) return null
      if (ps.hourStart == null || ps.hourEnd == null) return null
      const dayNames = [
        '', // 0 unused
        this.$t('org-dashboard.day-mon'),
        this.$t('org-dashboard.day-tue'),
        this.$t('org-dashboard.day-wed'),
        this.$t('org-dashboard.day-thu'),
        this.$t('org-dashboard.day-fri'),
        this.$t('org-dashboard.day-sat'),
        this.$t('org-dashboard.day-sun'),
      ]
      const day = dayNames[ps.day] || ''
      return `${day} ${ps.hourStart}h-${ps.hourEnd}h`
    },
  },
}
</script>

<style lang="scss" scoped>
.organization-dashboard {
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
}

.dashboard-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-grid {
  display: grid;
  gap: 12px;
}

.kpi-grid--tab {
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));

  @media (max-width: 959px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 960px) and (max-width: 1263px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.heatmap-insight {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #92400E;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 8px 14px;
}

.efficiency-quality-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: stretch;

  @media (min-width: 1264px) {
    grid-template-columns: 1fr 1fr;
  }

  > * {
    min-height: 0;
  }
}

.fleet-summary-line {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6B7280;
  padding: 0 4px;
}

.eligible-only-note {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #9CA3AF;
  font-style: italic;
  padding: 0 4px;
}

.fleet-actions-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 1264px) {
    grid-template-columns: 1fr 1fr;
  }
}

.fleet-col,
.actions-col {
  min-height: 380px;
}

.ops-row-health {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 1264px) {
    grid-template-columns: 2fr 1fr;
  }
}

.ops-col-health,
.ops-col-reliability {
  min-width: 0;
}

.ops-row-trend-quality {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: center;

  @media (min-width: 1264px) {
    grid-template-columns: 1fr 1fr;
  }
}

.ops-col-trend,
.ops-col-quality {
  min-width: 0;
}

.quality-revenue-usage-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.section-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 12px;
  margin-top: 32px;

  &:first-child {
    margin-top: 0;
  }

  &--esg {
    margin-top: 48px;
    border-bottom-color: #a7f3d0;
  }
}

.section-divider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-divider-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-divider-subtitle {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
  font-weight: 400;
}

.performance-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 1264px) {
    grid-template-columns: 1fr 1fr;
  }
}

.dashboard-loading {
  padding: 20px 0;
}

.dashboard-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;

  p {
    font-size: 14px;
    color: #6b7280;
  }
}

.dashboard-tabs {
  margin-top: 24px;

  ::v-deep .v-tab {
    font-size: 13px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    min-width: auto;
    padding: 0 16px;
  }

  ::v-deep .v-tab--active {
    color: #FF5722;
  }
}

.dashboard-tabs-items {
  background: transparent !important;

  ::v-deep .v-window__container {
    min-height: 400px;
  }
}
</style>
